// src/dq/SdhReviewWorkspace.tsx
// DQ Review cockpit — rule-centric SME decision surface.
// One count system (rules, not tasks). Impact shown inline on every card. Evidence on the card.
// Reads/writes exclusively through sdhAPI (/adriva_api/dq/sdh). Drop-in replacement — same props.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SmeHelp from "./SmeHelp";
import DashboardTab from "./DashboardTab";
import { HelpProvider, HelpDot, Pill, dimensionOf } from "./HelpKit";
import { useAuth } from "../hooks/useAuth";
import SmeImportPanel from "./SmeImportPanel";
import IterationTab from "./IterationTab";
import RuleStudioPanel from "./RuleStudioPanel";
import FieldValuesPanel from "./FieldValuesPanel";
import RuleVersionsPanel from "./RuleVersionsPanel";
import SampleRecordsPanel from "./SampleRecordsPanel";
import { measurementOf, NotMeasured, impactText } from "./measurement";
import SdhFieldView from "./SdhFieldView";
import {
  sdh_rules, sdh_applyDecisions, sdh_ruleDetail, sdh_preview, sdh_completeness,
  sdh_dataProfile, sdh_dataProfileColumn, sdh_conformanceHistory, sdh_exportRulebook,
  type EnrichedRule, type ProfileTable, type ProfileColumn, type ProfileColumnDetail,
  type ConformanceRun, type CompletenessFinding,
  SME_ASSESSMENTS, ASSESSMENT_BY_VALUE, GATE_TONE, sdh_smeTaxonomy, type AssessmentDef,
} from "../api/sdhAPI";

/* ─────────────────────────── helpers ─────────────────────────── */
const fmt = (n?: number | null) => (n == null ? "—" : Number(n).toLocaleString());
const pctf = (x?: number | null) => (x == null ? "—" : `${(x * 100).toFixed(x > 0 && x < 0.999 ? 1 : 0)}%`);

type Scope = "cross-table" | "cross-field" | "single";
function scopeOf(r: EnrichedRule): Scope {
  const raw: any = r._raw || {};
  const pf: any[] = raw.participating_fields || [];
  const tables = new Set<string>();
  const fields = new Set<string>();
  const own = String(r.owner_field || raw.owner_field_id || "");
  if (own.includes("::")) tables.add(own.split("::")[0].toUpperCase());
  pf.forEach((p) => {
    const fid = String(p.field_id || "");
    if (fid) fields.add(fid.toUpperCase());
    if (fid.includes("::")) tables.add(fid.split("::")[0].toUpperCase());
  });
  if (String(raw.execution_scope || r.execution_scope) === "cross_table" || tables.size >= 2) return "cross-table";
  return fields.size >= 2 ? "cross-field" : "single";
}
const FAM_LABEL: Record<string, string> = {
  conditional_mandatory: "Conditional", conditional_exclusion: "Exclusion", conditional_inclusion: "Inclusion",
  code_domain: "Value set", comparison: "Comparison", referential: "Reference", mandatory: "Mandatory",
  range: "Range", uniqueness: "Uniqueness", mutual_exclusion: "Exclusion", unit_conversion: "Unit",
  formula: "Formula", completeness: "Data gap",
};
type Auth = "confirmed" | "observed" | "inferred" | "none";
function authOf(r: EnrichedRule): Auth {
  const raw: any = r._raw || {};
  const la = (r as any).rule_logic_authority || raw.rule_logic_authority;
  if (la === "client_confirmed") return "confirmed";
  const ps = (r as any).policy_status || raw.policy_status;
  if (ps === "backed_confirmed") return "confirmed";
  // deterministic/structural rules are derived from observed profiling data — treat as observed, not "no policy"
  if (ps === "backed_observed" || ps === "structural_no_policy" || la === "observed") return "observed";
  if (ps === "backed_inferred" || la === "inferred") return "inferred";
  return "none";
}
const AUTH_LABEL: Record<Auth, string> = { confirmed: "client-confirmed", observed: "observed", inferred: "inferred", none: "no policy" };
const lifecycleOf = (s?: string) =>
  ({ AUTO_APPROVED: "Approved", SME_APPROVED: "Approved", PENDING: "Draft", REJECTED: "Rejected" } as any)[s || ""] || "Draft";

// Single source for turning a chosen assessment into its decision gate — used by the counts preview,
// the card tint, the tray breakdown and the save payload, so gate logic lives in exactly one place.
const gateOf = (assessment?: string): "" | "APPROVED" | "CONDITIONAL" | "PENDING" | "REJECTED" =>
  (assessment && (ASSESSMENT_BY_VALUE[assessment]?.gate as any)) || "";

type GateKey = "ALL" | "NOT_REVIEWED" | "APPROVED" | "CONDITIONAL" | "PENDING" | "REJECTED";
// The rule's PERSISTED gate. Prefers the SME's recorded decision; falls back to the legacy
// approval_status so rules decided before the four-gate rollout still bucket correctly.
const gateOfRule = (r: EnrichedRule): Exclude<GateKey, "ALL"> => {
  const g = (r as any).sme_decision?.decision_gate;
  if (g === "APPROVED" || g === "CONDITIONAL" || g === "PENDING" || g === "REJECTED") return g;
  const life = lifecycleOf(r.approval_status);
  if (life === "Approved") return "APPROVED";
  if (life === "Rejected") return "REJECTED";
  return "NOT_REVIEWED";
};
const GATE_META: { key: GateKey; label: string; tone: string }[] = [
  { key: "ALL",          label: "All",          tone: "#42586e" },
  { key: "NOT_REVIEWED", label: "Not reviewed", tone: "#8a97a3" },
  { key: "APPROVED",     label: "Approved",     tone: "#2f7a4f" },
  { key: "CONDITIONAL",  label: "Conditional",  tone: "#946011" },
  { key: "PENDING",      label: "Pending",      tone: "#43607e" },
  { key: "REJECTED",     label: "Rejected",     tone: "#b23a30" },
];

// Compact card affordance: shows the pending decision (if any) and opens the popover.
function DecideButton({ draft, current, onOpen }:
  { draft?: { assessment: string; comment: string }; current?: any; onOpen: () => void }) {
  const val = draft?.assessment || current?.assessment;
  const def = val ? ASSESSMENT_BY_VALUE[val] : undefined;
  return (
    <button className={"dqc-decide" + (draft ? " pending" : "")}
      style={def ? { ["--t" as any]: GATE_TONE[def.gate] } : undefined} onClick={onOpen}>
      {def ? <><span className="dot" />{def.label}{draft ? " (unsaved)" : ""}</> : "Decide…"}
      <span className="cv">▾</span>
    </button>
  );
}

// The SME's decision control: a compact popover. Two steps — pick the gate, then the reason + a comment.
// A comment is REQUIRED for every decision (including Approve) so nothing is ever recorded without a why.
function DecisionPopover({ value, comment, onCommit, onCancel, anchorLabel, defs }:
  { value?: string; comment?: string; onCommit: (assessment: string, comment: string) => void;
    onCancel: () => void; anchorLabel?: string; defs?: AssessmentDef[] }) {
  const ALL = defs && defs.length ? defs : SME_ASSESSMENTS;
  const [a, setA] = useState<string>(value || "");
  const [c, setC] = useState<string>(comment || "");
  const [g, setG] = useState<string>(value ? (ASSESSMENT_BY_VALUE[value]?.gate as string) : "");
  const opts = ALL.filter((x) => x.gate === g);
  const def = a ? ASSESSMENT_BY_VALUE[a] : undefined;
  const ready = !!a && c.trim().length >= 3;
  const hint = g === "APPROVED" ? "Why is this correct? (recorded with the approval)"
    : g === "CONDITIONAL" ? "What must change before this can be enforced?"
    : g === "PENDING" ? "What needs confirming, and by whom?"
    : "Why is this rule not right?";
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", esc); return () => window.removeEventListener("keydown", esc);
  }, [onCancel]);
  return (
    <>
      <div className="dqc-pop-scrim" onClick={onCancel} />
      <div className="dqc-pop" onClick={(e) => e.stopPropagation()}>
        <div className="dqc-pop-h">{anchorLabel || "Decision"}<button className="x" onClick={onCancel}>×</button></div>
        <div className="dqc-pop-gates">
          {GATE_META.filter((x) => x.key !== "ALL" && x.key !== "NOT_REVIEWED").map((x) => (
            <button key={x.key} className={"gbtn" + (g === x.key ? " on" : "")} style={{ ["--t" as any]: x.tone }}
              onClick={() => { setG(x.key); setA(""); }}>{x.label}</button>
          ))}
        </div>
        {g && (
          <div className="dqc-pop-body">
            <div className="lbl">Reason</div>
            {opts.map((o) => (
              <label key={o.value} className={"ropt" + (a === o.value ? " on" : "")}>
                <input type="radio" name="assess" checked={a === o.value} onChange={() => setA(o.value)} />
                <span>{o.label}{o.condition ? <em> — clears when {o.condition}</em> : null}</span>
              </label>
            ))}
            <div className="lbl req">Comment <b>*</b></div>
            <textarea className="dqc-pop-ta" rows={3} placeholder={hint} value={c} onChange={(e) => setC(e.target.value)} />
            {!ready && <div className="warn">{!a ? "Pick a reason." : "A comment is required (min 3 characters)."}</div>}
          </div>
        )}
        <div className="dqc-pop-f">
          <button className="dqc-btn info" onClick={onCancel}>Cancel</button>
          <button className="dqc-btn app" disabled={!ready}
            style={def ? { background: GATE_TONE[def.gate] } : undefined}
            onClick={() => onCommit(a, c.trim())}>Confirm</button>
        </div>
      </div>
    </>
  );
}

/** The SME's decision, shown wherever a rule appears. Deliberately NO "re-review" prompt: a rule only
 *  changes when the agent revises it, and applying an Excel decision is not a change to the rule. */
function DecisionBadge({ r }: { r: EnrichedRule }) {
  const dec = (r as any).sme_decision || {};
  const gate = dec.decision_gate as keyof typeof GATE_TONE | undefined;
  if (!gate) return null;
  const label = ASSESSMENT_BY_VALUE[dec.assessment]?.label || String(gate);
  const who = dec.sme_name || dec.sme_id;
  const when = dec.decided_at ? String(dec.decided_at).slice(0, 10) : "";
  return (
    <span className="dqc-dec" style={{ ["--t" as any]: GATE_TONE[gate] }}
      title={[label, who && `by ${who}`, when].filter(Boolean).join(" · ")}>
      <span className="dot" />{label}
    </span>
  );
}

type ImpactState = "flags" | "pass" | "unmeasured" | "unsupported" | "not_applicable";
function impactOf(r: EnrichedRule): { state: ImpactState; pass?: number | null; viol?: number | null; appl?: number | null; note?: string | null } {
  // Prefer the pipeline-stamped impact (from the sample run) so cards match the details modal.
  const ia: any = (r as any).review_classification?.impact || (r as any)._raw?.impact_assessment;
  const note: string | null = (r as any).tag_notes?.execution || (r as any).impact_note || ia?.note || ia?.reason || ia?.error || null;
  if (ia && ia.executed) {
    const viol = ia.violations, appl = ia.applicable_rows;
    const pass = ia.violation_rate != null ? 1 - ia.violation_rate : (viol != null && appl ? 1 - viol / appl : 1);
    if (viol != null && viol > 0) return { state: "flags", pass, viol, appl, note };
    return { state: "pass", pass: pass ?? 1, viol: 0, appl, note };
  }
  // Ran, but the rule's condition matched no rows in this extract → not "unmeasured", it's "N/A here".
  if (ia && ia.executed === false && ia.applicable_rows === 0) return { state: "not_applicable", appl: 0, note };
  const st = r.impact_status;
  if (st === "UNSUPPORTED") return { state: "unsupported" };
  const viol = r.impact_violations;
  if (st && viol != null && viol > 0) return { state: "flags", pass: r.impact_pass_rate, viol, appl: r.impact_applicable_rows };
  if (st) return { state: "pass", pass: r.impact_pass_rate ?? 1, viol: 0, appl: r.impact_applicable_rows };
  return { state: "unmeasured" };
}

/* ─────────────────────────── shell ─────────────────────────── */
type TabKey = "review" | "byfield" | "profile" | "dashboard" | "iteration" | "help";
const TABS: { key: TabKey; label: string }[] = [
  { key: "review", label: "Review" },
  { key: "byfield", label: "By field" },
  { key: "profile", label: "Data profile" },
  { key: "dashboard", label: "Dashboard" },
  { key: "iteration", label: "Agent revisions" },
  { key: "help", label: "SME Help" },
];

export default function SdhReviewWorkspace(
  { entityId, entityLabel, appNm }: { entityId: string; entityLabel?: string; appNm: string }
) {
  const [tab, setTab] = useState<TabKey>("review");
  const [flash, setFlash] = useState<string | null>(null);
  const [detail, setDetail] = useState<EnrichedRule | null>(null);
  const [importing, setImporting] = useState(false);
  const [studioFor, setStudioFor] = useState<EnrichedRule | null>(null);
  const [fieldFor, setFieldFor] = useState<{ table: string; column: string } | null>(null);
  const [versionsFor, setVersionsFor] = useState<EnrichedRule | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Decision vocabulary is fetched ONCE here and shared with the cockpit and the detail modal, so new
  // assessments added on the backend appear everywhere with no UI change.
  const [assessDefs, setAssessDefs] = useState<AssessmentDef[]>(SME_ASSESSMENTS);
  useEffect(() => { sdh_smeTaxonomy().then(setAssessDefs).catch(() => {}); }, []);
  const note = (m: string) => { setFlash(m); setTimeout(() => setFlash(null), 2600); };

  const openDetail = useCallback(async (ruleId: string) => {
    try { const r = await sdh_ruleDetail(entityId, ruleId); setDetail(r.rule); }
    catch { note("Could not load rule detail"); }
  }, [entityId]);

  const doExport = async () => {
    try { note("Building workbook…"); await sdh_exportRulebook(entityId, appNm); note("Downloaded."); }
    catch { note("Export failed"); }
  };

  return (
    <HelpProvider>
    <div className="dqc">
      <style>{CSS}</style>
      <div className="dqc-top">
        <div>
          <div className="dqc-title">Review rules — {entityLabel || "Product master data"}</div>
          <div className="dqc-sub">Approve the rules that reflect your business logic. Each shows the evidence behind it and what it flags on your live data.</div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="dqc-btn out" onClick={doExport}>⬇ Export rulebook</button>
        <button className="dqc-btn out" onClick={() => setImporting(true)}>⬆ Import reviewed file</button>
      </div>

      <div className="dqc-tabs">
        {TABS.map((t) => (
          <button key={t.key} className={"dqc-tab" + (tab === t.key ? " on" : "")} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      <div className="dqc-body">
        {tab === "review" && <ReviewCockpit key={reloadKey} entityId={entityId} appNm={appNm} onOpen={openDetail} onNote={note} assessDefs={assessDefs} />}
        {tab === "byfield" && <SdhFieldView entityId={entityId} onOpenRule={openDetail} />}
        {tab === "profile" && <ProfileTab entityId={entityId} />}
        {tab === "dashboard" && <DashboardTab entityId={entityId} onOpen={openDetail} />}
        {tab === "iteration" && <IterationTab entityId={entityId} appNm={appNm} onOpen={openDetail} onNote={note} sessionId={entityId} />}
        {tab === "help" && <SmeHelp />}
      </div>

      {flash && <div className="dqc-flash">{flash}</div>}
      {importing && (
        <SmeImportPanel entityId={entityId} appNm={appNm}
          onClose={() => setImporting(false)}
          onApplied={(m) => { setImporting(false); note(m); setReloadKey((k) => k + 1); }} />
      )}
      {versionsFor && (
        <RuleVersionsPanel entityId={entityId} appNm={appNm}
          constraintId={versionsFor.rule_id} ruleName={versionsFor.rule_name}
          onClose={() => setVersionsFor(null)}
          onChanged={(m) => { note(m); setDetail(null); setVersionsFor(null); setReloadKey((k) => k + 1); }} />
      )}
      {fieldFor && (
        <FieldValuesPanel entityId={entityId} table={fieldFor.table} column={fieldFor.column}
          onClose={() => setFieldFor(null)} />
      )}
      {studioFor && (
        <RuleStudioPanel entityId={entityId} appNm={appNm} rule={studioFor}
          onClose={() => setStudioFor(null)}
          onSaved={(m) => { setStudioFor(null); setDetail(null); note(m); setReloadKey((k) => k + 1); }} />
      )}
      {detail && (
        <RuleDetailModal entityId={entityId} appNm={appNm} rule={detail} defs={assessDefs}
          onRefine={() => setStudioFor(detail)}
          onFieldValues={(t, c) => setFieldFor({ table: t, column: c })}
          onVersions={() => setVersionsFor(detail)}
          onClose={() => setDetail(null)} onDone={(m) => { note(m); setDetail(null); }} />
      )}
    </div>
    </HelpProvider>
  );
}

/* ─────────────────────────── review cockpit ─────────────────────────── */
type Draft = { assessment: string; comment: string };
function ReviewCockpit({ entityId, appNm, onOpen, onNote, assessDefs }:
  { entityId: string; appNm: string; onOpen: (id: string) => void; onNote: (m: string) => void;
    assessDefs: AssessmentDef[] }) {
  const { user } = useAuth();
  const smeId = user?.user_id || user?.email || "unknown";
  const [rules, setRules] = useState<EnrichedRule[]>([]);
  const [counts, setCounts] = useState<{ All: number; Approved: number; Draft: number; Rejected: number }>({ All: 0, Approved: 0, Draft: 0, Rejected: 0 });
  const [decisions, setDecisions] = useState<Record<string, Draft>>({});
  const [previewed, setPreviewed] = useState<Record<string, EnrichedRule>>({});
  const [gate, setGate] = useState<GateKey>("NOT_REVIEWED");
  const [assessFilter, setAssessFilter] = useState<string | null>(null);
  const [popFor, setPopFor] = useState<string | null>(null);
  const [bulkPop, setBulkPop] = useState(false);
  // A reason belonging to another gate must not stay active after switching gates.
  useEffect(() => {
    if (assessFilter && gate !== "ALL" && ASSESSMENT_BY_VALUE[assessFilter]?.gate !== gate) setAssessFilter(null);
  }, [gate, assessFilter]);
  const [field, setField] = useState<string | null>(null);
  const [family, setFamily] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope | null>(null);
  const [trust, setTrust] = useState<string | null>(null);
  const [risk, setRisk] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [onlyViol, setOnlyViol] = useState(false);
  const [onlyLookup, setOnlyLookup] = useState(false);
  const [onlyClientOk, setOnlyClientOk] = useState(false);
  const [tagFilter, setTagFilter] = useState<{ lens: string; state: string } | null>(null);
  // sidebar sections collapse by default; STATUS stays open (it's the main driver)
  const [openSec, setOpenSec] = useState<Record<string, boolean>>({ Status: true });
  const toggleSec = (k: string) => setOpenSec((s) => ({ ...s, [k]: !s[k] }));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [gaps, setGaps] = useState<CompletenessFinding[]>([]);
  const [showGaps, setShowGaps] = useState(false);

  const load = useCallback(async () => {
    try { const r = await sdh_rules(entityId, "All"); setRules(r.rules || []); setCounts(r.counts as any || counts); }
    catch { onNote("Could not load rules"); }
    try { const g = await sdh_completeness(entityId); setGaps(g.findings || []); } catch { /* endpoint optional */ }
  }, [entityId]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const view = (r: EnrichedRule) => previewed[r.rule_id] || r;
  const tableOf = (r: EnrichedRule) => String(r.owner_field || (r._raw || {}).owner_field_id || "::").split("::")[0].toUpperCase();
  const fieldOf = (r: EnrichedRule) => String(r.owner_field || (r._raw || {}).owner_field_id || "::").split("::").pop() || "";
  const labelOf = (r: EnrichedRule) => (r as any).owner_label || fieldOf(r);

  const gapCount = useMemo(() => rules.filter((r) => authOf(r) === "none").length, [rules]);


  // one filter predicate; `ignore` lets each facet count reflect the OTHER active filters (faceted search)
  const passes = useCallback((r: EnrichedRule, ignore?: string) => {
    if (ignore !== "table" && field && tableOf(r) !== field) return false;
    if (ignore !== "family" && family && (FAM_LABEL[r.family || ""] || r.family) !== family) return false;
    if (ignore !== "scope" && scope && scopeOf(r) !== scope) return false;
    const rc: any = (r as any).review_classification || {};
    if (ignore !== "trust" && trust && rc.trust !== trust) return false;
    if (ignore !== "risk" && risk && rc.risk !== risk) return false;
    if (ignore !== "reason" && reason && rc.review_label !== reason) return false;
    if (ignore !== "gate" && gate !== "ALL" && gateOfRule(r) !== gate) return false;
    if (ignore !== "assess" && assessFilter && (r as any).sme_decision?.assessment !== assessFilter) return false;
    if (onlyViol && impactOf(view(r)).state !== "flags") return false;
    if (onlyLookup && !(r as any).requires_reference_data
        && !String((r as any).reference_data_name || "").length
        && (r.family !== "code_domain" && r.family !== "lookup" && r.family !== "referential")) return false;
    if (onlyClientOk && !((r as any).policy_status === "backed_confirmed" || (r as any).rule_logic_authority === "client_confirmed")) return false;
    if (ignore !== "tag" && tagFilter) {
      const rv = tagFilter.lens === "dimension" ? dimensionOf(r) : ((r as any).tags?.[tagFilter.lens]);
      if (rv !== (tagFilter.state === "true" ? true : tagFilter.state)) return false;
    }
    if (q) { const s = `${r.rule_name} ${fieldOf(r)} ${r.business_logic} ${r.rule_id}`.toLowerCase(); if (!s.includes(q.toLowerCase())) return false; }
    return true;
  }, [field, family, scope, trust, risk, reason, gate, assessFilter, onlyViol, onlyLookup, onlyClientOk, tagFilter, q, previewed]); // eslint-disable-line

  const byTag = useCallback((lens: string) => {
    const t: Record<string, number> = {};
    for (const r of rules.filter((rr) => passes(rr, "tag"))) {
      const v = lens === "dimension" ? dimensionOf(r) : (r as any).tags?.[lens];
      if (v === undefined || v === null || v === false) continue;
      const key = v === true ? "true" : String(v);
      t[key] = (t[key] || 0) + 1;
    }
    return t;
  }, [rules, passes]);

  const byTable = useMemo(() => tally(rules.filter((r) => passes(r, "table")).map(tableOf)), [rules, passes]);
  const byFam = useMemo(() => tally(rules.filter((r) => passes(r, "family")).map((r) => FAM_LABEL[r.family || ""] || r.family || "—")), [rules, passes]);
  const byScope = useMemo(() => tally(rules.filter((r) => passes(r, "scope")).map(scopeOf)), [rules, passes]);
  const rcOf = (r: EnrichedRule) => (r as any).review_classification || {};
  const byTrust = useMemo(() => tally(rules.filter((r) => passes(r, "trust")).map((r) => rcOf(r).trust || "—")), [rules, passes]);
  const byRisk = useMemo(() => tally(rules.filter((r) => passes(r, "risk")).map((r) => rcOf(r).risk || "—")), [rules, passes]);
  const byReason = useMemo(() => tally(rules.filter((r) => passes(r, "reason")).map((r) => rcOf(r).review_label || "—")), [rules, passes]);
  // faceted counts for the top decision bar (respect all OTHER active filters)
  // Gate segment counts: ignore the gate filter itself so all segments stay visible.
  const gatePool = useMemo(() => rules.filter((r) => passes(r, "gate")), [rules, passes]);
  const gateCount = (g: GateKey) => g === "ALL" ? gatePool.length : gatePool.filter((r) => gateOfRule(r) === g).length;
  // Reason counts: RESPECT the selected gate (ignore only the assessment filter), so picking "Approved"
  // shows only Approved reasons — the sidebar section is genuinely dynamic to the current gate.
  const assessPool = useMemo(() => rules.filter((r) => passes(r, "assess")), [rules, passes]);
  const assessCounts = useMemo(() => {
    const t: Record<string, number> = {};
    for (const r of assessPool) {
      const a = (r as any).sme_decision?.assessment; if (a) t[a] = (t[a] || 0) + 1;
    }
    return t;
  }, [assessPool]);

  const shown = useMemo(() => rules.filter((r) => passes(r)), [rules, passes]);
  const grouped = useMemo(() => groupBy(shown, fieldOf), [shown]);

  const commitDecision = (id: string, assessment: string, comment: string) =>
    setDecisions((d) => ({ ...d, [id]: { assessment, comment } }));
  const bulkApply = (assessment: string, comment: string) => setDecisions((d) => {
    const next = { ...d };
    for (const r of shown) next[r.rule_id] = { assessment, comment };
    return next;
  });
  const clearAll = () => setDecisions({});
  const saveAll = async () => {
    const payload = Object.entries(decisions).map(([id, dr]) => {
      const def = ASSESSMENT_BY_VALUE[dr.assessment];
      return {
        constraint_id: id, decision_type: "rule_review",
        choice: def?.choice || "reject_rule",
        assessment: dr.assessment, decision_gate: def?.gate, comment: dr.comment || "",
        sme_id: smeId, sme_name: user?.full_name || user?.email || undefined,
      };
    });
    if (!payload.length) return;
    setBusy(true);
    try { await sdh_applyDecisions(entityId, appNm, payload); onNote(`Saved ${payload.length} decision(s).`); setDecisions({}); await load(); }
    catch { onNote("Save failed"); } finally { setBusy(false); }
  };
  const runPreview = async (r: EnrichedRule) => {
    onNote(`Measuring ${labelOf(r)}…`);
    try {
      const res: any = await sdh_preview(entityId, appNm, [r.rule_id], "all");
      const { hit, compiled, note } = readPreview(res, r.rule_id);
      if (hit) {
        setPreviewed((p) => ({ ...p, [r.rule_id]: { ...r,
          impact_status: hit.status || ((hit.violating_rows ?? hit.violations ?? 0) > 0 ? "FAIL" : "PASS"),
          impact_violations: hit.violating_rows ?? hit.violations ?? 0,
          impact_applicable_rows: hit.applicable_rows ?? hit.total_rows ?? null,
          impact_pass_rate: hit.pass_rate ?? null } }));
      } else {
        // compiler has no executor for this family (e.g. formula / unit) — say so instead of failing
        setPreviewed((p) => ({ ...p, [r.rule_id]: { ...r, impact_status: "UNSUPPORTED" } }));
        onNote(compiled === 0 ? "This rule type can’t be auto-measured yet." : (note || "No measurable rows for this rule."));
      }
    } catch { onNote("Preview failed"); }
  };

  const pendingN = Object.keys(decisions).length;
  const pendingGates = Object.values(decisions).reduce((m, d) => {
    const g = gateOf(d.assessment) || "REJECTED"; m[g] = (m[g] || 0) + 1; return m;
  }, {} as Record<string, number>);

  return (
    <div className="dqc-grid">
      {/* counts */}
      <div className="dqc-full">
        {/* Two-level decision filter: gate segments (level 1) → assessment chips (level 2).
            Replaces the old STATUS sidebar section and the static counts tiles. */}
        <div className="dqc-gatebar">
          {GATE_META.map((g) => {
            const n = gateCount(g.key);
            return (
              <button key={g.key} className={"dqc-gseg" + (gate === g.key ? " on" : "")}
                style={{ ["--t" as any]: g.tone }}
                onClick={() => { setGate(g.key); setAssessFilter(null); }}>
                <span className="n">{n}</span><span className="l">{g.label}</span>
              </button>
            );
          })}
        </div>
        <div className="dqc-prog" title="Decision progress across all rules">
          {(() => {
            const tot = rules.length || 1;
            const w = (g: GateKey) => `${(rules.filter((r) => gateOfRule(r) === g).length / tot) * 100}%`;
            return <>
              <i className="pa" style={{ width: w("APPROVED") }} />
              <i className="pc" style={{ width: w("CONDITIONAL") }} />
              <i className="pp" style={{ width: w("PENDING") }} />
              <i className="pr" style={{ width: w("REJECTED") }} />
            </>;
          })()}
        </div>
      </div>

      {/* rail */}
      <aside className="dqc-rail" style={{ overflowY: "auto", maxHeight: "calc(100vh - 80px)", position: "sticky", top: 0 }}>
        {(() => {
          const TAG_LABELS: Record<string, string> = {
            measured_clean: "Passed (clean)", measured_flags: "Flags data",
            not_measured_needs_values: "Needs governed values", not_measured_config: "Needs config",
            not_measured_uncompilable: "Engine can't compile yet",
            not_measured_no_rows: "No matching rows", not_measured_error: "Engine error",
            bound_governed: "Governed set bound", values_pending: "Values pending", not_a_valueset: "—",
            not_challenged: "Not reviewed", validated: "Reviewer validated", refined: "Refined (applied)",
            unresolved_objection: "Unresolved objection", recommend_drop: "Reviewer: drop",
            values_client_governed: "Values client-governed", column_client_governed: "Column governed · values pending",
            client_rule_confirmed: "Client-rule confirmed", measured_observed: "Observed evidence", none: "—",
            true: "Likely rule defect",
          };
          const Sec = ({ title, k, children, always }: any) => (
            <div className="dqc-sec">
              {always ? <h4>{title}</h4> :
                <h4 className="dqc-sec-h" onClick={() => toggleSec(k)}><span>{title}</span><span className="chev">{openSec[k] ? "▼" : "▶"}</span></h4>}
              {(always || openSec[k]) && <div>{children}</div>}
            </div>
          );
          const tagSec = (title: string, k: string, lens: string, order?: string[], help?: string) => {
            const counts = byTag(lens); const keys = (order || Object.keys(counts)).filter((x) => counts[x]);
            if (!keys.length) return null;
            const head = help ? <span>{title}<HelpDot topic={help} /></span> : title;
            return <Sec title={head} k={k}>
              {keys.map((st) => (
                <Opt key={st} label={TAG_LABELS[st] || st} count={counts[st]}
                  on={tagFilter?.lens === lens && tagFilter?.state === st}
                  onClick={() => setTagFilter(tagFilter?.lens === lens && tagFilter?.state === st ? null : { lens, state: st })} />
              ))}
            </Sec>;
          };
          return <>
            {/* Decision status now lives in the top decision bar (two-level gate → reason). */}

            {/* DECISION REASON — dynamic: rendered from the backend taxonomy, so new assessments
                appear automatically with no UI change. Grouped by gate, counts respect other filters. */}
            {Object.keys(assessCounts).length > 0 && (
              <Sec title={<span>Decision reason<HelpDot topic="reviewer" /></span>} k="dreason">
                {(["APPROVED", "CONDITIONAL", "PENDING", "REJECTED"] as const).map((g) => {
                  const items = assessDefs.filter((a) => a.gate === g && assessCounts[a.value]);
                  if (!items.length) return null;
                  return (
                    <div key={g} className="dqc-rgrp">
                      <div className="dqc-rgrp-h" style={{ color: GATE_TONE[g] }}>{g.toLowerCase()}</div>
                      {items.map((a) => (
                        <Opt key={a.value} label={a.label.replace(/^[^—]+—\s*/, "")} count={assessCounts[a.value]}
                          on={assessFilter === a.value}
                          onClick={() => setAssessFilter(assessFilter === a.value ? null : a.value)} />
                      ))}
                    </div>
                  );
                })}
              </Sec>
            )}

            {/* DQ DIMENSION — the quality question each rule answers */}
            {tagSec("Quality dimension", "dim", "dimension",
              ["Completeness", "Validity", "Conformity", "Consistency", "Integrity", "Uniqueness"], "dimension")}

            {/* MEASUREMENT — did it run on your data, and what did it find */}
            {tagSec("Measurement", "meas", "execution_state",
              ["measured_flags", "measured_clean", "not_measured_needs_values", "not_measured_uncompilable", "not_measured_no_rows", "not_measured_error"], "measurement")}

            {/* REVIEWER — challenge / objection / refine (correct, split) */}
            {tagSec("Reviewer", "rev", "challenge_state",
              ["unresolved_objection", "recommend_drop", "refined", "validated", "not_challenged"], "reviewer")}

            {/* LIKELY DEFECTS — over-broad flaggers */}
            {(() => { const c = byTag("suspect_overbroad"); return c["true"] ?
              <Sec title={<span>Attention<HelpDot topic="attention" /></span>} k="att"><Opt label="⚠ Likely rule defect" count={c["true"]}
                on={tagFilter?.lens === "suspect_overbroad" && tagFilter?.state === "true"}
                onClick={() => setTagFilter(tagFilter?.lens === "suspect_overbroad" ? null : { lens: "suspect_overbroad", state: "true" })} /></Sec> : null; })()}

            {/* AUTHORITY — where the rule's trust comes from */}
            {tagSec("Authority", "auth", "authority_state",
              ["client_rule_confirmed", "values_client_governed", "column_client_governed", "measured_observed"], "authority")}

            {/* REFERENCE DATA — governed value-set state */}
            {tagSec("Reference data", "ref", "reference_state", ["bound_governed", "values_pending"], "measurement")}

            {gate !== "APPROVED" && <>
              <Sec title="Prioritise · Trust" k="trust">
                {["Confirmed", "Supported", "Tentative", "Unverified"].filter((t) => byTrust[t]).map((t) => (
                  <Opt key={t} label={t} count={byTrust[t]} on={trust === t} onClick={() => setTrust(trust === t ? null : t)} />
                ))}
              </Sec>
              <Sec title="Prioritise · Risk" k="risk">
                {["High", "Moderate", "Low"].filter((t) => byRisk[t]).map((t) => (
                  <Opt key={t} label={t + " risk"} count={byRisk[t]} on={risk === t} onClick={() => setRisk(risk === t ? null : t)} />
                ))}
              </Sec>
              <Sec title="Prioritise · What to do" k="todo">
                {Object.keys(byReason).sort((a, b) => byReason[b] - byReason[a]).map((t) => (
                  <Opt key={t} label={t} count={byReason[t]} on={reason === t} onClick={() => setReason(reason === t ? null : t)} />
                ))}
              </Sec>
            </>}
            <Sec title="Table" k="table">
              {Object.keys(byTable).sort().map((t) => (
                <Opt key={t} label={t} count={byTable[t]} on={field === t} onClick={() => setField(field === t ? null : t)} />
              ))}
            </Sec>
            <Sec title="Rule type" k="ruletype">
              {Object.keys(byFam).sort().map((t) => (
                <Opt key={t} label={t} count={byFam[t]} on={family === t} onClick={() => setFamily(family === t ? null : t)} />
              ))}
            </Sec>
            <Sec title="Scope" k="scope">
              {(["cross-table", "cross-field", "single"] as Scope[]).map((s) => (
                <Opt key={s} label={s} count={byScope[s] || 0} on={scope === s} onClick={() => setScope(scope === s ? null : s)} />
              ))}
            </Sec>
          </>;
        })()}
        <div className="dqc-gap">
          <b>⚑ {gapCount} coverage items</b>
          <p>Novel rules with no control in your library yet — candidates to adopt as policy.</p>
        </div>
        {gaps.length > 0 && (
          <div className="dqc-gap datagap" onClick={() => setShowGaps(true)}>
            <b>▤ {gaps.length} data gaps</b>
            <p>Governed fields that are ~100% empty. Not rules — click to review the report.</p>
          </div>
        )}
      </aside>

      {/* list */}
      <main className="dqc-list">
        <div className="dqc-toolbar">
          <div className="dqc-search">🔎<input value={q} placeholder="Search rules, fields, logic…" onChange={(e) => setQ(e.target.value)} /></div>
          <button className={"dqc-toggle" + (onlyViol ? " on" : "")} onClick={() => setOnlyViol((v) => !v)}>⚠ Only rules that flag data</button>
          <button className={"dqc-toggle" + (onlyLookup ? " on" : "")} onClick={() => setOnlyLookup((v) => !v)} title="Rules that validate a value against a governed lookup / reference-data list">▤ Reference-data lookups</button>
          <button className={"dqc-toggle" + (onlyClientOk ? " on" : "")} onClick={() => setOnlyClientOk((v) => !v)} title="Rules confirmed by a client-authored data rule (highest trust)">✓ Client-confirmed</button>
        </div>
        <div className="dqc-resultbar">
          <span className="cnt">Showing <b>{shown.length}</b> of {rules.length} rules</span>
          {shown.length > 1 && gate !== "APPROVED" && (
            <span className="dqc-bulk">
              <button className="dqc-decide" onClick={() => setBulkPop(true)}>Apply to all {shown.length} shown…<span className="cv">▾</span></button>
            </span>
          )}
          {(() => {
            const chips = [
              gate !== "ALL" ? { k: "gate", label: GATE_META.find(g => g.key === gate)?.label || gate, clear: () => { setGate("ALL"); setAssessFilter(null); } } : null,
              assessFilter ? { k: "assess", label: ASSESSMENT_BY_VALUE[assessFilter]?.label || assessFilter, clear: () => setAssessFilter(null) } : null,
              field ? { k: "table", label: `Table: ${field}`, clear: () => setField(null) } : null,
              family ? { k: "family", label: `Type: ${family}`, clear: () => setFamily(null) } : null,
              scope ? { k: "scope", label: `Scope: ${scope}`, clear: () => setScope(null) } : null,
              trust ? { k: "trust", label: `Trust: ${trust}`, clear: () => setTrust(null) } : null,
              risk ? { k: "risk", label: `Risk: ${risk}`, clear: () => setRisk(null) } : null,
              reason ? { k: "reason", label: `To do: ${reason}`, clear: () => setReason(null) } : null,
              onlyViol ? { k: "viol", label: "flags data", clear: () => setOnlyViol(false) } : null,
              onlyLookup ? { k: "lookup", label: "reference-data lookups", clear: () => setOnlyLookup(false) } : null,
              onlyClientOk ? { k: "clientok", label: "client-confirmed", clear: () => setOnlyClientOk(false) } : null,
              tagFilter ? { k: "tag", label: `${tagFilter.lens.replace(/_/g, " ").replace(" state", "")}: ${tagFilter.state}`, clear: () => setTagFilter(null) } : null,
              q ? { k: "q", label: `“${q}”`, clear: () => setQ("") } : null,
            ].filter(Boolean) as any[];
            if (!chips.length) return null;
            return <>
              {chips.map((c: any) => (
                <span key={c.k} className="dqc-fpill">{c.label}{" "}
                  <span className="x" role="button" title="Remove filter"
                    style={{ cursor: "pointer" }} onClick={c.clear}>×</span>
                </span>
              ))}
              {chips.length > 1 && (
                <button className="dqc-fclear" style={{ cursor: "pointer", marginLeft: 6 }}
                  onClick={() => { setGate("ALL"); setAssessFilter(null); setField(null); setFamily(null); setScope(null);
                    setTrust(null); setRisk(null); setReason(null); setOnlyViol(false); setOnlyLookup(false); setOnlyClientOk(false); setTagFilter(null); setQ(""); }}>
                  Clear all
                </button>
              )}
            </>;
          })()}
        </div>

        {shown.length === 0 ? (
          <div className="dqc-empty">No rules match these filters. Clear a filter to see more.</div>
        ) : Object.keys(grouped).sort().map((f) => (
          <div key={f}>
            <div className="dqc-fhead">
              <b className="fh-label">{grouped[f][0] && labelOf(grouped[f][0])}</b>
              <span className="fh-tech">{grouped[f][0] && tableOf(grouped[f][0])}.{f}</span>
              <span className="fh-n">{grouped[f].length} rule{grouped[f].length !== 1 ? "s" : ""}</span>
            </div>
            {grouped[f].map((r0) => {
              const r = view(r0); const im = impactOf(r); const au = authOf(r); const sc = scopeOf(r);
              const draft = decisions[r.rule_id]; const ch = gateOf(draft?.assessment); const raw: any = r._raw || {};
              const policy = au === "none"
                ? <span className="novel">novel — not in control library</span>
                : <>{AUTH_LABEL[au]}{r.business_control_id ? <> · <span className="phys">{r.business_control_id}</span></> : null}</>;
              return (
                <div key={r.rule_id} className={"dqc-card" + (ch ? " done-" + ch.toLowerCase() : "")}>
                  <div className="dqc-rname">
                    {r.rule_name || r.rule_id}
                    {draft && <span className="dqc-donetag" style={{ background: GATE_TONE[ASSESSMENT_BY_VALUE[draft.assessment]?.gate as keyof typeof GATE_TONE] || "#888" }}>{ASSESSMENT_BY_VALUE[draft.assessment]?.label || draft.assessment}</span>}
                    <DecisionBadge r={r} />
                    {(() => {   // a revised rule stays in Review under its decision — say so here
                      const rf: any = (r as any).refinement;
                      if (!rf?.action || rf.action === "KEEP") return null;
                      return <span className="dqc-revised" title={rf.explanation || "Revised by the agent"}>
                        ✎ agent revised{(r as any).version ? ` · v${(r as any).version}` : ""}</span>;
                    })()}
                  </div>
                  <div className="dqc-badges">
                    {(() => { const rc = (r as any).review_classification || {}; return <>
                      <span className={"b trust t-" + (rc.trust || "").toLowerCase()} title={rc.explain?.trust}>{rc.trust_label || "—"}</span>
                      <span className={"b risk k-" + (rc.risk || "").toLowerCase()} title={rc.explain?.risk}>{rc.risk_label} risk</span>
                      {rc.review_reason && <span className="b reason" title={rc.explain?.reason}>{rc.review_label}</span>}
                      {rc.recommend_quick_approve && <span className="b quick" title="Trust + risk make this safe to clear quickly — guidance only">★ quick-approve</span>}
                    </>; })()}
                    <span className="b fam">{FAM_LABEL[r.family || ""] || r.family}</span>
                    {(() => { const dv = dimensionOf(r); return dv ? <span className={"b dim dim-" + dv.toLowerCase()} title={`Data-quality dimension: ${dv}`}>{dv}</span> : null; })()}
                    {sc !== "single" && <span className={"b " + (sc === "cross-table" ? "xt" : "xf")}>{sc}</span>}
                    {(() => {
                      const t = (r as any).tags || {};
                      const cs = t.challenge_state;
                      if (cs === "unresolved_objection") return <span className="b rev-obj" title={(r as any).tag_notes?.challenge || "Reviewer raised an unresolved issue — needs your judgment"}>⚠ needs judgment</span>;
                      if (cs === "recommend_drop") return <span className="b rev-obj" title={(r as any).tag_notes?.challenge}>⚠ reviewer: drop</span>;
                      if (cs === "refined") return <span className="b rev-refined" title={(r as any).tag_notes?.challenge || "Reviewer refined this rule; change applied"}>✎ refined</span>;
                      if (cs === "validated") return <span className="b rev-ok" title="Reviewer checked and validated — no changes">✓ reviewed</span>;
                      return null;
                    })()}
                    {(r as any).tags?.suspect_overbroad && <span className="b suspect" title={(r as any).tag_notes?.suspect || "Flags an implausibly high share of rows — likely a rule defect, not a data issue"}>⚠ likely rule defect</span>}
                    {(() => {
                      const as_ = (r as any).tags?.authority_state;
                      if (as_ === "client_rule_confirmed") return <span className="b clientok" title={(r as any).tag_notes?.authority}>✓ client-confirmed</span>;
                      if (as_ === "values_client_governed") return <span className="b clientval" title={(r as any).tag_notes?.authority}>✓ client-governed values</span>;
                      if (as_ === "column_client_governed") return <span className="b clientpend" title={(r as any).tag_notes?.authority}>client-governed · values pending</span>;
                      return null;
                    })()}
                  </div>
                  {((r as any).plain || raw.business_reason || r.description) &&
                    <div className="dqc-why">{(r as any).plain || raw.business_reason || r.description}</div>}
                  {(() => {   // the reviewer's own words, where she can see them while triaging
                    const d: any = (r as any).sme_decision || {};
                    if (!d.comment) return null;
                    const g = d.decision_gate as keyof typeof GATE_TONE | undefined;
                    return <div className="dqc-smecmt" style={g ? { ["--t" as any]: GATE_TONE[g] } : undefined}>
                      <b>{ASSESSMENT_BY_VALUE[d.assessment]?.label || d.decision_gate}</b>
                      <span className="w">{d.sme_name || d.sme_id || "SME"}{d.decided_at ? ` · ${String(d.decided_at).slice(0,10)}` : ""}</span>
                      <div className="c">“{d.comment}”</div>
                    </div>;
                  })()}
                  {(r as any).review_note &&
                    <div className="dqc-note">{(r as any).review_note}</div>}
                  <div className="dqc-meta"><span>{policy}</span>
                    <span>field <b>{labelOf(r)}</b> <span className="phys">{tableOf(r)}.{fieldOf(r)}</span></span></div>

                  {/* impact — signature */}
                  {im.state === "unsupported" ? (
                    <div className="dqc-impact un">
                      <span className="lab">Not auto-measurable</span>
                      <span className="untext">This rule type isn’t executable in the engine yet — approve on the evidence, and it won’t block enforcement of the others.</span>
                    </div>
                  ) : im.state === "not_applicable" ? (
                    <div className="dqc-impact un">
                      <span className="lab">No rows to check</span>
                      <span className="untext">{im.note || "This rule ran, but its condition matches 0 records in the current data — there is nothing to flag here."} (Risk below is a structural estimate, not a measurement.)</span>
                    </div>
                  ) : im.state === "unmeasured" ? (
                    <div className="dqc-impact un">
                      <span className="lab">Not yet measured</span>
                      <span className="untext">See how many records this would flag before you approve.</span>
                      <button className="dqc-mini" onClick={() => runPreview(r0)}>Preview impact</button>
                    </div>
                  ) : !measurementOf(r).ok ? (
                    /* Numbers are only shown when they belong to THIS version of the rule. */
                    <div className="dqc-impact un"><NotMeasured m={measurementOf(r)} /></div>
                  ) : (
                    <div className="dqc-impact">
                      <div className="lab">
                        <span>Impact on live data</span>
                        {im.state === "flags" ? <span className="r">flags {fmt(im.viol)} of {fmt(im.appl)}</span> : <span className="g">clean</span>}
                      </div>
                      <div className="dqc-ibar">
                        <i className="pass" style={{ width: `${(im.pass ?? 1) * 100}%` }} />
                        <i className="flag" style={{ width: `${(1 - (im.pass ?? 1)) * 100}%` }} />
                      </div>
                      <div className="dqc-iline">
                        {im.state === "flags"
                          ? <><span className="r">{fmt(im.viol)}</span> records violate · <span className="g">{pctf(im.pass)}</span> pass — approving raises this as a finding</>
                          : <><span className="g">{pctf(im.pass)}</span> of {fmt(im.appl)} records pass — safe to enforce</>}
                      </div>
                    </div>
                  )}

                  <div className="dqc-acts">
                    {lifecycleOf(r.approval_status) === "Rejected" ? (
                      <>
                        <span className="dqc-rejreason">✕ Rejected{(r as any).rejected_by ? ` by ${(r as any).rejected_by}` : ""}{(r as any).rejection_reason ? ` — ${(r as any).rejection_reason}` : ""}</span>
                        <DecideButton draft={decisions[r.rule_id]} onOpen={() => setPopFor(r.rule_id)} />
                        <button className="dqc-btn info" onClick={() => onOpen(r.rule_id)}>Details & evidence</button>
                      </>
                    ) : (
                      <>
                        <DecideButton draft={decisions[r.rule_id]} onOpen={() => setPopFor(r.rule_id)} />
                        <button className="dqc-btn info" onClick={() => onOpen(r.rule_id)}>Details & evidence</button>
                        <span className="dqc-expand" onClick={(e) => togglePre(e)}>view logic ⌄</span>
                      </>
                    )}
                  </div>
                  <div className="dqc-detail">
                    <div className="k">What the rule checks</div>
                    <pre className="plainlogic">{humanizeLogic(r.business_logic, r) || (r as any).expected_outcome || "—"}</pre>
                    <div className="k">Rule id</div><pre>{r.rule_id}</pre>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>Open <b>Details &amp; evidence</b> for engine-generated SQL (MS-SQL/Oracle/Postgres) and the impact preview.</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </main>

      {showGaps && <DataGapsPanel gaps={gaps} onClose={() => setShowGaps(false)} />}

      {popFor && (() => {
        const r = rules.find((x) => x.rule_id === popFor);
        return <DecisionPopover defs={assessDefs} anchorLabel={r?.rule_name || popFor}
          value={decisions[popFor]?.assessment || (r as any)?.sme_decision?.assessment}
          comment={decisions[popFor]?.comment}
          onCancel={() => setPopFor(null)}
          onCommit={(a, c) => { commitDecision(popFor, a, c); setPopFor(null); }} />;
      })()}
      {bulkPop && (
        <DecisionPopover defs={assessDefs} anchorLabel={`Apply to all ${shown.length} shown rules`}
          onCancel={() => setBulkPop(false)}
          onCommit={(a, c) => { bulkApply(a, c); setBulkPop(false); }} />
      )}

      {/* decision tray */}
      {pendingN > 0 && (
        <div className="dqc-tray">
          <span className="t">{pendingN} pending — {pendingGates.APPROVED || 0} approve · {pendingGates.CONDITIONAL || 0} conditional · {pendingGates.PENDING || 0} pending · {pendingGates.REJECTED || 0} reject</span>
          <span style={{ flex: 1 }} />
          <button className="undo" onClick={clearAll}>Clear</button>
          <button className="save" disabled={busy} onClick={saveAll}>{busy ? "Saving…" : "Save decisions"}</button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── small bits ─────────────────────────── */
function Opt({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return <div className={"dqc-opt" + (on ? " on" : "")} onClick={onClick}><span>{label}</span><span className="ct">{count}</span></div>;
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
function tally(arr: string[]) { const m: Record<string, number> = {}; arr.forEach((k) => (m[k] = (m[k] || 0) + 1)); return m; }
function groupBy<T>(arr: T[], key: (t: T) => string) { const m: Record<string, T[]> = {}; arr.forEach((x) => { const k = key(x); (m[k] = m[k] || []).push(x); }); return m; }
function togglePre(e: React.MouseEvent) {
  const card = (e.currentTarget as HTMLElement).closest(".dqc-card"); const d = card?.querySelector(".dqc-detail");
  if (d) { d.classList.toggle("open"); (e.currentTarget as HTMLElement).textContent = d.classList.contains("open") ? "hide logic ⌃" : "view logic ⌄"; }
}
function readPreview(res: any, ruleId: string): { hit: any; compiled?: number; note?: string } {
  const pv = res?.preview || res || {};
  const list: any[] = pv.per_rule || res?.rule_results || res?.results || [];
  const hit = list.find((x) => (x.control_id || x.constraint_id || x.rule_id) === ruleId) || (list.length === 1 ? list[0] : null);
  return { hit, compiled: pv.compiled, note: pv.note };
}

/* ─────────────────────────── data profile ─────────────────────────── */
function ProfileTab({ entityId }: { entityId: string }) {
  const [tables, setTables] = useState<ProfileTable[]>([]);
  const [table, setTable] = useState<string>("");
  const [cols, setCols] = useState<ProfileColumn[]>([]);
  const [col, setCol] = useState<ProfileColumnDetail | null>(null);
  useEffect(() => { (async () => { try { const r = await sdh_dataProfile(entityId); setTables(r.tables || []); if (r.tables?.[0]) setTable(r.tables[0].table); } catch {} })(); }, [entityId]);
  useEffect(() => { if (!table) return; (async () => { try { const r = await sdh_dataProfile(entityId, table); setCols(r.columns || []); setCol(null); } catch {} })(); }, [entityId, table]);
  const openCol = async (c: ProfileColumn) => { try { setCol(await sdh_dataProfileColumn(entityId, c.table, c.column)); } catch {} };
  return (
    <div>
      <div className="dqc-pillrow">
        {tables.map((t) => <button key={t.table} className={"dqc-pill" + (table === t.table ? " on" : "")} onClick={() => setTable(t.table)}>{t.table} ({t.columns})</button>)}
      </div>
      <div style={{ display: "flex", gap: 16 }}>
        <div className="dqc-tablewrap" style={{ flex: 2 }}>
          <table className="dqc-table">
            <thead><tr><th>Column</th><th>Type</th><th>Distinct</th><th>Null %</th><th>Mandatory</th></tr></thead>
            <tbody>{cols.map((c) => (
              <tr key={c.column} className="dqc-trow" onClick={() => openCol(c)}>
                <td><b>{c.column}</b></td><td>{c.data_type}</td><td>{fmt(c.distinct)}</td><td>{c.null_pct}%</td><td>{c.mandatory ? "✓" : ""}</td>
              </tr>))}</tbody>
          </table>
        </div>
        <div style={{ flex: 1 }}>
          {col ? (
            <div className="dqc-panel">
              <b>{col.column}</b>
              <div className="dqc-muted">{col.data_type} · {fmt(col.records)} records · {col.null_pct}% null · {fmt(col.distinct)} distinct</div>
              <h5>Top values</h5>
              {col.top_values.map((v, i) => <div key={i} className="dqc-topval"><span>{v.value || "∅"}</span><span className="dqc-muted">{fmt(v.count)}</span></div>)}
            </div>
          ) : <div className="dqc-empty">Select a column to see its profile.</div>}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── run history ─────────────────────────── */
function HistoryTab({ entityId }: { entityId: string }) {
  const [runs, setRuns] = useState<ConformanceRun[]>([]);
  useEffect(() => { (async () => { try { const r = await sdh_conformanceHistory(entityId); setRuns(r.runs || []); } catch {} })(); }, [entityId]);
  return (
    <div className="dqc-tablewrap">
      <table className="dqc-table">
        <thead><tr><th>Run</th><th>Ruleset</th><th>When</th><th>Compiled</th><th>Headline</th></tr></thead>
        <tbody>{runs.map((r) => (
          <tr key={r.run_id} className="dqc-trow">
            <td><b>{r.run_id}</b></td><td>{r.ruleset_version}</td><td>{r.generated_at?.replace("T", " ").slice(0, 16)}</td>
            <td>{r.compiled_count}</td><td>{(r.conformance || {}).headline || "—"}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────── rule detail modal ─────────────────────────── */
function RuleDetailModal({ entityId, appNm, rule, onClose, onDone, defs, onRefine, onFieldValues,
                          onVersions }:
  { entityId: string; appNm: string; rule: EnrichedRule; onClose: () => void; onDone: (m: string) => void;
    defs?: AssessmentDef[]; onRefine?: () => void; onFieldValues?: (t: string, c: string) => void;
    onVersions?: () => void }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  // default SQL dialect = MS-SQL; can later be driven by an env/config value
  const [dialect, setDialect] = useState<"tsql" | "postgres" | "oracle" | "duckdb">(
    ((window as any).__DQ_SQL_DIALECT__ as any) || "tsql");
  const [prev, setPrev] = useState<{ viol?: number; appl?: number; pass?: number | null; state: string } | null>(null);
  const [showSamples, setShowSamples] = useState(false);
  const [mAssess, setMAssess] = useState<string>((rule as any).sme_decision?.assessment || "");
  const [mComment, setMComment] = useState<string>((rule as any).sme_decision?.comment || "");
  const [showHistory, setShowHistory] = useState(false);
  const [mPop, setMPop] = useState(false);
  const raw: any = rule._raw || {};
  const bundle: any = (rule as any).sql_bundle;
  const im = prev || impactOf(rule);
  const doPreview = async () => {
    setBusy(true); onDone("Measuring…");
    try {
      const res: any = await sdh_preview(entityId, appNm, [rule.rule_id], "all");
      const { hit, compiled } = readPreview(res, rule.rule_id);
      if (hit) {
        const viol = hit.violating_rows ?? hit.violations ?? 0;
        const appl = hit.applicable_rows ?? hit.total_rows ?? null;
        setPrev({ state: viol > 0 ? "flags" : "pass", viol, appl,
                  pass: hit.pass_rate ?? (appl ? 1 - viol / appl : 1) });
      } else onDone(compiled === 0 ? "This rule type can’t be auto-measured yet." : "No measurable rows.");
    } catch { onDone("Preview failed — showing the stored sample-run impact."); } finally { setBusy(false); }
  };
  const saveDecision = async () => {
    const def = mAssess ? ASSESSMENT_BY_VALUE[mAssess] : undefined;
    if (!def) { onDone("Pick a decision first."); return; }
    setBusy(true);
    try {
      await sdh_applyDecisions(entityId, appNm, [{
        constraint_id: rule.rule_id, decision_type: "rule_review",
        choice: def.choice, assessment: def.value, decision_gate: def.gate, comment: mComment,
        sme_id: user?.user_id || user?.email || "unknown",
        sme_name: user?.full_name || user?.email || undefined,
      }]);
      onDone(`Saved: ${def.label}`);
    } catch { onDone("Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="dqc-modalbg" onClick={onClose}>
      <div className="dqc-modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="dqc-modalh">
          <div>
            <div className="dqc-muted" style={{ color: "#cdd8ea" }}>RULE DETAILS</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--dqc-disp)" }}>{rule.rule_name || rule.rule_id}</div>
            <div style={{ opacity: .8, fontFamily: "var(--dqc-mono)", fontSize: 12 }}>{rule.rule_id}</div>
            {(() => { const dv = dimensionOf(rule); return dv ? (
              <div style={{ marginTop: 7 }}><span className={"b dim dim-" + dv.toLowerCase()} style={{ fontSize: 10.5, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>{dv}</span>
                <span style={{ marginLeft: 6 }}><HelpDot topic="dimension" /></span></div>
            ) : null; })()}
          </div>
          <button className="dqc-x" onClick={onClose}>×</button>
        </div>
        <div className="dqc-modalbody">
          {/* SME REVIEW — the reviewer's own decision leads the panel. The long trust/risk essays that
              used to sit here duplicated the SME Help page; the badges keep their tooltips and the "?"
              opens Help, so nothing is lost and the space now carries reviewer-specific information. */}
          {(() => {
            const dec: any = (rule as any).sme_decision || {};
            const rc: any = (rule as any).review_classification || {};
            const gate = dec.decision_gate as keyof typeof GATE_TONE | undefined;
            if (!gate && !rc.trust) return null;
            return (
              <div className="dqc-review">
                {gate ? (
                  <div className="dqc-smebox" style={{ ["--t" as any]: GATE_TONE[gate] }}>
                    <div className="hd">
                      <span className="gate">{String(gate)}</span>
                      <span className="cat">{ASSESSMENT_BY_VALUE[dec.assessment]?.label || dec.assessment || ""}</span>
                      <span className="who">
                        {dec.sme_name || dec.sme_id || "SME"}
                        {dec.decided_at ? ` · ${String(dec.decided_at).replace("T", " ").slice(0, 16)}` : ""}
                        {(rule as any).version ? ` · reviewed at v${dec.reviewed_version ?? (rule as any).version}` : ""}
                      </span>
                    </div>
                    {dec.comment && <p className="cmt">“{dec.comment}”</p>}
                  </div>
                ) : (
                  <div className="dqc-smebox none"><div className="hd"><span className="gate">NOT REVIEWED</span>
                    <span className="cat">No decision recorded yet</span></div></div>
                )}
                {rc.trust && (
                  <div className="dqc-triage">
                    <span className={"chip trust t-" + String(rc.trust).toLowerCase()} title={rc.explain?.trust}><b>Trust</b> {rc.trust_label}</span>
                    <span className={"chip risk k-" + String(rc.risk).toLowerCase()} title={rc.explain?.risk}><b>Risk</b> {rc.risk_label}</span>
                    {rc.challenge_severity && rc.challenge_severity !== "None" && <span className="chip sev" title={rc.explain?.reason}><b>Objection</b> {rc.challenge_severity}</span>}
                    {rc.review_reason && <span className="chip" title={rc.explain?.reason}>{rc.review_label}</span>}
                    <HelpDot topic="trust" />
                  </div>
                )}
              </div>
            );
          })()}
          <div className="k">Impact on live data <span className="dqc-muted" style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(sample run)</span><HelpDot topic="measurement" /></div>
          {(() => {
            const ia: any = (rule as any).review_classification?.impact || (rule as any)._raw?.impact_assessment || {};
            const measured = prev || (ia.executed ? { appl: ia.applicable_rows, viol: ia.violations, rate: ia.violation_rate } : null);
            if (!measured && im.state === "unsupported")
              return <p className="dqc-muted">This rule type isn’t executable in the engine yet.</p>;
            if (!measured && im.state === "not_applicable")
              return <div className="dqc-impact un" style={{ marginBottom: 4 }}>
                <span className="untext">{(im as any).note || `Executed on the sample, but the rule’s condition matches 0 of ${fmt((ia.applicable_rows ?? 0) || rule.impact_applicable_rows)} rows in this data — nothing to flag here.`}</span>
              </div>;
            if (!measured && (ia.error || ia.status === "not_executable") && im.state !== "not_applicable")
              return <div className="dqc-impact un" style={{ marginBottom: 4, borderLeft: "3px solid var(--r,#c0392b)", paddingLeft: 8 }}>
                <span className="untext">{(rule as any).tag_notes?.execution || ia.note || ia.reason || ia.error || "This rule could not be executed — see the reason above; it stays in review and won’t block others."}</span>
              </div>;
            if (!measured && im.state === "unmeasured")
              return <div className="dqc-impact un" style={{ marginBottom: 4 }}>
                <span className="untext">Not assessed yet — run the impact assessment step, or preview here.</span>
                {bundle && <button className="dqc-mini" disabled={busy} onClick={doPreview}>Preview impact</button>}
              </div>;
            // Final gate: a fresh live preview (`prev`) is always trustworthy; otherwise the stored
            // numbers must belong to THIS version of the rule. Never fall back to a previous version's
            // figures just because the fields still hold them.
            const mm = measurementOf(rule);
            if (!prev && !mm.ok) {
              return <div style={{ marginBottom: 6 }}>
                <NotMeasured m={mm} />
                {bundle && <button className="dqc-mini" style={{ marginTop: 8 }} disabled={busy}
                  onClick={doPreview}>Measure this version now</button>}
              </div>;
            }
            const appl = (measured as any).appl ?? (im as any).appl ?? rule.impact_applicable_rows;
            const viol = (measured as any).viol ?? (im as any).viol ?? rule.impact_violations;
            const rate = (measured as any).rate;
            return <>
              <div className="dqc-kpis">
                <div><span>{fmt(appl)}</span><small>records checked</small></div>
                <div><span className={viol ? "r" : "g"}>{fmt(viol)}</span><small>flagged</small></div>
                <div><span className={viol ? "r" : "g"}>{rate != null ? (rate * 100).toFixed(1) + "%" : pctf((im as any).pass ?? rule.impact_pass_rate)}</span><small>{rate != null ? "violation rate" : "pass rate"}</small></div>
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {bundle && <button className="dqc-mini" disabled={busy} onClick={doPreview}>Re-run preview</button>}
                {/* Right where a violation rate raises "but what IS in that field?" */}
                {(() => {
                  const own = String((rule as any).owner_field || (rule as any)._raw?.owner_field_id || "");
                  const [t, c] = own.split("::");
                  return t && c ? <button className="dqc-mini vals" onClick={() => onFieldValues?.(t, c)}
                    title="Profiled values, your reference data, and what was accepted">⌕ What values are in {c}?</button> : null;
                })()}
              </div>
            </>;
          })()}
          {(() => {
            const rc: any = (rule as any).review_classification?.impact || {};
            const raw: any = (rule as any)._raw || {};
            const ia: any = raw.impact_assessment || {};
            // pick the first NON-EMPTY array — an empty [] is truthy in JS, so `||` alone would
            // stop at the first (empty) source and never reach the impact_assessment fallback.
            const pick = (...arrs: any[]) => arrs.find((a) => Array.isArray(a) && a.length) || [];
            const fails: any[] = pick((rule as any).impact_sample_fail, ia.sample_fail_records, rc.sample_fail_records);
            const passes: any[] = pick((rule as any).impact_sample_pass, ia.sample_pass_records, rc.sample_pass_records);
            if (!fails.length && !passes.length) return null;
            return <div className="dqc-samples">
              <button className="dqc-sampletoggle" onClick={() => setShowSamples((v) => !v)}>
                <span className="chev">{showSamples ? "▾" : "▸"}</span>
                <span>Sample records</span>
                <span className="dqc-samplecount">
                  {fails.length ? <em className="f">{fails.length} failing</em> : null}
                  {passes.length ? <em className="p">{passes.length} passing</em> : null}
                </span>
                <span className="hint">click to inspect the actual rows</span>
              </button>
              {showSamples && (
                <SampleRecordsPanel
                  title={rule.rule_name || rule.rule_id}
                  subtitle={(rule as any).owner_field || raw.owner_field_id}
                  fails={fails} passes={passes}
                  ruleFields={(raw.participating_fields || []).map((p: any) => p.field_id).filter(Boolean)}
                  onClose={() => setShowSamples(false)} />
              )}
            </div>;
          })()}
          {(rule as any).disposition_reason &&
            <p className="dqc-dispo">Needs your review — {(rule as any).disposition_reason}</p>}

          <div className="dqc-mgrid">
            <section className="dqc-mcol">
              <div className="k">What it enforces</div>
              <p className="dqc-p">{humanizeLogic((rule as any).expected_outcome, rule) || (rule as any).plain || rule.description || "—"}</p>

              <div className="k">Rule logic <span className="dqc-muted" style={{ fontWeight: 400 }}>(readable pseudocode)</span></div>
              <pre className="dqc-pseudo">{formatPseudo(rule)}</pre>

              {(() => {
                const bi: any = (rule as any).business_impact;
                const items: string[] = Array.isArray(bi) ? bi.filter(Boolean) : (bi ? [String(bi)] : []);
                if (!items.length) return null;
                return <>
                  <div className="k">Why it matters</div>
                  <ul className="dqc-ul">{items.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </>;
              })()}

              {hasCaveat(rule) && (
                <div className="dqc-caveat">
                  <div className="ch">⚠ The model’s own caveat <span className="verdict">{(rule as any).challenge?.verdict}</span></div>
                  {(rule as any).challenge?.issue && <p>{(rule as any).challenge.issue}</p>}
                  {(rule as any).challenge_rationale && <p className="rat">{(rule as any).challenge_rationale}</p>}
                </div>
              )}
            </section>

            <section className="dqc-mcol">
              <div className="k">Data evidence</div>
              {evidenceLines(rule).length
                ? evidenceLines(rule).map((l, i) => <p key={i} className="dqc-ev">{l}</p>)
                : <p className="dqc-muted">No measured evidence captured yet.</p>}

              <div className="k">Attribution</div>
              <div className="dqc-kv">
                <div className="kv"><span className="kk">Fields involved<br /><em className="hintk">click to see values</em></span>
                  <span className="kvv">{meaningfulFields(rule).map((f, i) => (
                    <button key={i} className="dqc-fchip act" title="See this field's values: profiled, client-supplied and accepted"
                      onClick={() => { const [t, c] = String(f.fid).split("::"); if (t && c) onFieldValues?.(t, c); }}>
                      <span className="ic">⌕</span>{f.label} <span className="phys">{f.fid.replace("::", ".")}</span>
                    </button>
                  ))}</span></div>
                <div className="kv"><span className="kk">Values &amp; profiling</span>
                  <span className="kvv">
                    {meaningfulFields(rule).map((f, i) => {
                      const [t, c] = String(f.fid).split("::");
                      if (!t || !c) return null;
                      return <button key={i} className="dqc-valbtn"
                        onClick={() => onFieldValues?.(t, c)}
                        title={`What values does ${t}.${c} actually contain? Profiled data, your reference data, and what was accepted.`}>
                        ⌕ {c} — what values exist?
                      </button>;
                    })}
                  </span></div>
                <div className="kv"><span className="kk">Authority</span><span className="kvv">{authText(rule)}</span></div>
                <div className="kv"><span className="kk">Policy backing</span><span className="kvv">{authOf(rule) === "none" ? "novel — not in control library" : (rule.business_control_id || "linked control")}</span></div>
                {(rule as any).policy_basis && <div className="kv"><span className="kk">Policy basis</span><span className="kvv">{(rule as any).policy_basis}</span></div>}
                <div className="kv"><span className="kk">Trust</span><span className="kvv">{(rule as any).review_classification?.trust_label || "—"}</span></div>
              </div>
              {(() => {
                const as_ = (rule as any).tags?.authority_state;
                const head = as_ === "client_rule_confirmed" ? "Confirmed by your data-dictionary rule"
                  : as_ === "values_client_governed" ? "Values governed by client reference data"
                  : as_ === "column_client_governed" ? "Column governed by client — values pending"
                  : ((rule as any).policy_status === "backed_confirmed" || (rule as any).rule_logic_authority === "client_confirmed") ? "Backed by client-confirmed policy" : null;
                if (!head) return null;
                const pending = as_ === "column_client_governed";
                return (
                  <div className={"dqc-clientok" + (pending ? " pending" : "")}>
                    <div className="k">{pending ? "◑" : "✓"} {head}</div>
                    {(rule as any).tag_notes?.authority && <p className="dqc-p">{(rule as any).tag_notes.authority}</p>}
                    {(rule as any).policy_citation && <p className="dqc-p">{String((rule as any).policy_citation)}</p>}
                    {(rule as any).business_control_id && <p className="dqc-p"><b>Evidence:</b> control <code>{(rule as any).business_control_id}</code></p>}
                  </div>
                );
              })()}
              {(() => {
                const cc = (rule as any).field_notes, kg = (rule as any).known_gaps;
                if (!cc && !kg) return null;
                return <div className="dqc-fieldnotes" style={{ marginTop: 10, padding: "8px 10px", borderLeft: "3px solid var(--dqc-accent,#0a7a6f)", background: "var(--dqc-soft,#f5faf9)", borderRadius: 6 }}>
                  <div className="k" style={{ marginBottom: 4 }}>Field notes <span className="dqc-muted" style={{ fontWeight: 400 }}>(client data dictionary)</span></div>
                  {cc && <p className="dqc-p" style={{ margin: "2px 0" }}>{String(cc).slice(0, 400)}</p>}
                  {kg && <p className="dqc-p" style={{ margin: "2px 0", color: "var(--dqc-warn,#a15c00)" }}><b>Known data gap:</b> {String(kg).slice(0, 300)}</p>}
                </div>;
              })()}

              <div className="k" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span>Executable SQL <span className="dqc-muted" style={{ fontWeight: 400 }}>(engine-generated)</span></span>
                {bundle && (
                  <select className="dqc-dialect" value={dialect} onChange={(e) => setDialect(e.target.value as any)}>
                    <option value="tsql">MS-SQL</option>
                    <option value="postgres">PostgreSQL</option>
                    <option value="oracle">Oracle</option>
                    <option value="duckdb">DuckDB</option>
                  </select>
                )}
              </div>
              {bundle?.[dialect]?.sample_query
                ? <pre className="dqc-code">{bundle[dialect].sample_query}</pre>
                : <p className="dqc-muted">This rule type isn’t compiled to SQL yet — see “What it enforces”.</p>}
            </section>
          </div>
        </div>
        <div className="dqc-modalf">
          <div className="dqc-ver">
            <span className="v">v{(rule as any).version ?? 1}</span>
            {(() => {
              const n = ((rule as any).revisions?.length || 0) + ((rule as any).decision_history?.length || 0);
              return n > 0 ? <button className="hlink" onClick={() => setShowHistory((v) => !v)}>{showHistory ? "hide history" : `history (${n})`}</button> : null;
            })()}
            {((rule as any).revisions?.length || 0) > 1 && onVersions &&
              <button className="hlink" onClick={onVersions}
                title="See every version with its own content and impact, and switch the active one">
                versions ({(rule as any).revisions.length})</button>}
            {(rule as any).sme_decision?.decided_at &&
              <span className="last" title={`by ${(rule as any).sme_decision?.sme_id || "SME"}`}>last: {ASSESSMENT_BY_VALUE[(rule as any).sme_decision?.assessment]?.label || (rule as any).sme_decision?.choice} · {String((rule as any).sme_decision?.decided_at).slice(0, 10)}</span>}
          </div>
          <span style={{ flex: 1 }} />
          {onRefine && <button className="dqc-btn info" onClick={onRefine} title="Send this rule to the agent with your instruction">✨ Refine with agent</button>}
          <DecideButton draft={mAssess ? { assessment: mAssess, comment: mComment } : undefined}
            current={(rule as any).sme_decision} onOpen={() => setMPop(true)} />
          <button className="dqc-btn app" disabled={busy || !mAssess || !mComment.trim()} onClick={saveDecision}>{busy ? "Saving…" : "Save decision"}</button>
        </div>
        {mPop && <DecisionPopover defs={defs} anchorLabel={rule.rule_name || rule.rule_id}
          value={mAssess} comment={mComment} onCancel={() => setMPop(false)}
          onCommit={(a, c) => { setMAssess(a); setMComment(c); setMPop(false); }} />}
        {showHistory && (
          <div className="dqc-history">
            {(() => {
              // One chronological trail: how the RULE changed (revisions[]) + how the SME's DECISION
              // changed (the append-only ledger slice). Answers "she changed her mind — when and why?".
              const revs = ((rule as any).revisions || []).map((r: any) => ({
                kind: "rule" as const, at: r.at, version: r.version,
                label: r.change_type, text: r.summary, who: r.author,
              }));
              const decs = ((rule as any).decision_history || []).map((d: any) => ({
                kind: "decision" as const, at: d.decided_at, version: d.reviewed_version,
                label: d.decision_gate, text: d.comment || ASSESSMENT_BY_VALUE[d.assessment]?.label || d.choice,
                who: d.sme_id, assessment: d.assessment,
              }));
              const all = [...revs, ...decs].sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
              if (!all.length) return <div className="h-empty">No history yet.</div>;
              return all.map((e: any, i: number) => (
                <div className={"h-row " + e.kind} key={i}>
                  <span className="hv">v{e.version ?? "—"}</span>
                  <span className="hc" style={e.kind === "decision"
                    ? { color: GATE_TONE[e.label as keyof typeof GATE_TONE] || undefined } : undefined}>
                    {e.kind === "decision"
                      ? (ASSESSMENT_BY_VALUE[e.assessment]?.label || e.label)
                      : e.label}
                  </span>
                  <span className="hs">{e.text}</span>
                  <span className="ha">{e.who}{e.at ? " · " + String(e.at).slice(0, 10) : ""}</span>
                </div>
              ));
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
function Row({ k, v }: { k: string; v?: any }) {
  return <div className="dqc-row2"><span className="rk">{k}</span><span className="rv">{v == null || v === "" ? "—" : String(v)}</span></div>;
}
function hasCaveat(rule: EnrichedRule): boolean {
  const ch: any = (rule as any).challenge;
  const v = String(ch?.verdict || "").toLowerCase();
  return !!ch && (v === "revise" || v === "reject" || !!ch.issue);
}
function evidenceLines(rule: EnrichedRule): string[] {
  const out: string[] = [];
  const ed: any = (rule as any).evidence_detail;
  if (ed && typeof ed === "object") {
    if (ed.value != null) out.push(`Condition “${ed.value}” holds on ${Number(ed.support || 0).toLocaleString()} records; target field is filled ${ed.fill_rate != null ? Math.round(ed.fill_rate * 100) + "%" : "—"} of the time (verdict: ${ed.verdict || "—"}).`);
  }
  const av: any[] = (rule as any).allowed_values;
  if (Array.isArray(av) && av.length) out.push(`Governed values (${av.length}): ${av.slice(0, 18).join(", ")}${av.length > 18 ? " …" : ""}.`);
  const ood: any[] = (rule as any).out_of_domain_values;
  if (Array.isArray(ood) && ood.length) out.push(`Found off-list in your data: ${ood.join(", ")} — ${Number((rule as any).out_of_domain_row_count || 0).toLocaleString()} record(s).`);
  return out;
}
function meaningfulFields(rule: EnrichedRule): { label: string; fid: string }[] {
  const fl: any[] = (rule as any).field_labels || [];
  const seen = new Set<string>();
  const out: { label: string; fid: string }[] = [];
  fl.forEach((f) => {
    const role = String(f.role || "").toLowerCase();
    if (role === "join" || role === "grain") return;       // plumbing, not business fields
    const fid = String(f.field_id || "");
    if (!fid || seen.has(fid)) return;
    seen.add(fid);
    out.push({ label: f.label || fid.split("::").pop(), fid });
  });
  if (!out.length) {
    const own = String(rule.owner_field || "");
    out.push({ label: (rule as any).owner_label || own.split("::").pop(), fid: own });
  }
  return out;
}
function humanizeLogic(text?: string | null, rule?: EnrichedRule): string {
  if (!text) return "";
  const fl: any[] = ((rule as any)?.field_labels) || [];
  const pairs: [string, string][] = [];
  fl.forEach((f) => {
    const fid = String(f.field_id || "");
    if (!fid.includes("::") || !f.label) return;
    const [t, c] = fid.split("::");
    const rep = `${t}."${f.label}"`;                 // consistent: <table>."Display Name"
    pairs.push([`${t}."${c}"`, rep], [`${t}::${c}`, rep], [`${t}.${c}`, rep]);
  });
  pairs.sort((a, b) => b[0].length - a[0].length);   // longest tokens first (quoted before bare)
  let out = String(text);
  pairs.forEach(([tok, rep]) => { out = out.split(tok).join(rep); });
  return out;
}

// Pretty-print the rule's CASE-WHEN pseudo-code (business field names, kept as-is) for readability.
// Deterministic English proved lossy on compound predicates, so we format the pseudo-code instead.
function formatPseudo(rule?: EnrichedRule): string {
  if (!rule) return "—";
  const raw: any = (rule as any)._raw || rule;
  // Prefer the CASE-WHEN condition (business_logic / predicate) — the SELECT-style sql_bundle.readable
  // is a query, not the rule logic, and doesn't format into readable pseudocode.
  let s = humanizeLogic((rule as any).business_logic || raw.business_logic || raw.predicate_template, rule)
        || (rule as any).sql_bundle?.readable;
  if (!s) { const eo = humanizeLogic((rule as any).expected_outcome, rule); return eo || rule.description || "—"; }
  s = String(s).replace(/\s+/g, " ").trim();
  s = s
    .replace(/\bCASE\b/g, "CASE")
    .replace(/\s*\bWHEN\b/g, "\n  WHEN ")
    .replace(/\s+\bAND\b/g, "\n    AND ")
    .replace(/\s+\bOR\b/g, "\n    OR ")
    .replace(/\s*\bTHEN\b/g, "\n  THEN ")
    .replace(/\s*\bELSE\b/g, "\n  ELSE ")
    .replace(/\s*\bEND\b/g, "\nEND")
    .replace(/\bSELECT\b/g, "SELECT")
    .replace(/\s+\bFROM\b/g, "\nFROM ")
    .replace(/\s+\bWHERE\b/g, "\nWHERE ");
  return s.replace(/ +\n/g, "\n").replace(/\n{2,}/g, "\n").trim();
}
function authText(rule: EnrichedRule): string {
  const a = authOf(rule);
  return { confirmed: "Client-confirmed policy", observed: "Observed in your data", inferred: "Inferred from enterprise knowledge", none: "Novel — needs your confirmation" }[a];
}

/* ─────────────────────────── styles ─────────────────────────── */
function DataGapsPanel({ gaps, onClose }: { gaps: CompletenessFinding[]; onClose: () => void }) {
  const [q, setQ] = useState("");
  const byTable = groupBy(gaps.filter((g) => !q || `${g.label} ${g.column} ${g.table}`.toLowerCase().includes(q.toLowerCase())), (g) => g.table);
  return (
    <div className="dqc-modalbg" onClick={onClose}>
      <div className="dqc-modal" onClick={(e) => e.stopPropagation()} style={{ width: "min(760px,96vw)" }}>
        <div className="dqc-modalh">
          <div>
            <div className="dqc-muted" style={{ color: "#cdd8ea" }}>DATA-COMPLETENESS REPORT</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "var(--dqc-disp)" }}>{gaps.length} governed fields are unpopulated</div>
            <div style={{ opacity: .8, fontSize: 12, color: "#cdd8ea" }}>Defined/controlled by the model but ~100% empty in your data. A data-maintenance finding, not a rule to approve.</div>
          </div>
          <button className="dqc-x" onClick={onClose}>×</button>
        </div>
        <div className="dqc-modalbody">
          <div className="dqc-search" style={{ marginBottom: 12 }}>🔎<input value={q} placeholder="Find a field…" onChange={(e) => setQ(e.target.value)} /></div>
          {Object.keys(byTable).sort().map((t) => (
            <div key={t} style={{ marginBottom: 14 }}>
              <div className="dqc-fhead">{t} · {byTable[t].length}</div>
              <table className="dqc-table">
                <thead><tr><th>Field</th><th>Physical</th><th>Empty</th></tr></thead>
                <tbody>{byTable[t].map((g) => (
                  <tr key={g.field_id}>
                    <td><b>{g.label || g.column}</b></td>
                    <td className="dqc-muted" style={{ fontFamily: "var(--dqc-mono)" }}>{g.table}.{g.column}</td>
                    <td style={{ fontFamily: "var(--dqc-mono)", color: "var(--red)" }}>{g.null_pct}%</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CSS = `
.dqc{--ink:#0E2436;--ink2:#33475b;--muted:#6b7c8d;--line:#e3e8ee;--paper:#f5f7f9;--card:#fff;
 --teal:#0e7c7b;--teal-d:#0b5f5e;--amber:#b7791f;--amber-bg:#fdf6e7;--red:#c8433b;--red-bg:#fdeceb;
 --green:#1f9d6b;--brand:#6d4aff;--dqc-disp:'Space Grotesk',system-ui,sans-serif;--dqc-mono:'JetBrains Mono',ui-monospace,monospace;
 font-family:'Inter',system-ui,sans-serif;color:var(--ink);font-size:14px}
.dqc-top{display:flex;align-items:flex-start;gap:14px;padding:18px 4px 12px}
.dqc-title{font-family:var(--dqc-disp);font-size:20px;font-weight:600}
.dqc-sub{color:var(--muted);font-size:12.5px;margin-top:2px;max-width:640px}
.dqc-btn{font-weight:600;font-size:13px;border-radius:8px;padding:8px 15px;cursor:pointer;border:1px solid transparent;font-family:inherit}
.dqc-btn.out{background:#fff;border-color:var(--line);color:var(--ink2)} .dqc-btn.out:hover{background:var(--paper)}
.dqc-tabs{display:flex;gap:4px;border-bottom:1px solid var(--line);margin-bottom:16px}
.dqc-tab{background:none;border:0;padding:10px 14px;font-size:13.5px;color:var(--muted);cursor:pointer;border-bottom:2px solid transparent;font-family:inherit}
.dqc-tab.on{color:var(--ink);border-bottom-color:var(--brand);font-weight:600}
.dqc-grid{display:grid;grid-template-columns:232px 1fr;gap:22px;align-items:start}
.dqc-full{grid-column:1/-1}
.dqc-counts{display:flex;border:1px solid var(--line);border-radius:12px;overflow:hidden;background:var(--card);max-width:720px}
.dqc-counts .c{flex:1;padding:12px 16px;border-right:1px solid var(--line)} .dqc-counts .c:last-child{border-right:0}
.dqc-counts .n{font-family:var(--dqc-mono);font-size:22px;font-weight:600}
.dqc-counts .l{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.07em}
.dqc-counts .rev .n{color:var(--amber)} .dqc-counts .app .n{color:var(--teal)} .dqc-counts .rej .n{color:var(--red)}
.dqc-prog{max-width:720px;height:6px;background:var(--line);border-radius:6px;margin-top:9px;overflow:hidden;display:flex}
.dqc-prog i{display:block;height:100%} .dqc-prog .pa{background:#2f7a4f} .dqc-prog .pc{background:#b7791f} .dqc-prog .pp{background:#43607e} .dqc-prog .pr{background:var(--red)}
.dqc-rail{position:sticky;top:12px;background:var(--card);border:1px solid var(--line);border-radius:14px;padding:6px 8px 10px;box-shadow:0 1px 2px rgba(16,36,54,.03)}
.dqc-rail h4{font-size:10px;letter-spacing:.11em;text-transform:uppercase;color:#9aa6b2;font-weight:700;margin:0;padding:11px 8px 5px} .dqc-rail h4:first-child{margin-top:0}
.dqc-opt{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:6px 10px;border-radius:8px;cursor:pointer;font-size:13px;color:var(--ink2);position:relative;transition:background .12s ease,color .12s ease}
.dqc-opt:hover{background:#f4f7fa;color:var(--ink)}
.dqc-opt.on{background:#eef2f6;color:var(--ink);font-weight:600}
.dqc-opt.on::before{content:"";position:absolute;left:2px;top:7px;bottom:7px;width:3px;border-radius:3px;background:var(--teal)}
.dqc-opt .ct{font-family:var(--dqc-mono);font-size:11px;color:#a4b0bc;font-variant-numeric:tabular-nums}
.dqc-opt:hover .ct{color:#7c8794} .dqc-opt.on .ct{color:var(--teal)}
.dqc-gap{margin:12px 6px 0;border:1px solid #ead9b4;background:#fbf6ea;border-radius:10px;padding:10px 12px}
.dqc-gap b{font-family:var(--dqc-disp);font-size:12px;color:#8a6516} .dqc-gap p{margin:4px 0 0;font-size:11.5px;color:#907546;line-height:1.45}
.dqc-gap.datagap{border-color:#f0cdc8;background:#fdf1ef;cursor:pointer} .dqc-gap.datagap b{color:var(--red)} .dqc-gap.datagap p{color:#9a5750}
.dqc-gap.datagap:hover{background:#fbe6e2}
.dqc-toolbar{display:flex;gap:10px;align-items:center;margin-bottom:12px;flex-wrap:wrap}
.dqc-search{flex:1;min-width:180px;display:flex;align-items:center;gap:8px;background:var(--card);border:1px solid var(--line);border-radius:9px;padding:8px 12px}
.dqc-search input{border:0;outline:0;flex:1;font-size:13px;background:transparent;font-family:inherit}
.dqc-toggle{font-size:12.5px;color:var(--ink2);background:var(--card);border:1px solid var(--line);border-radius:9px;padding:8px 12px;cursor:pointer;font-family:inherit}
.dqc-toggle.on{border-color:var(--red);color:var(--red);background:var(--red-bg)}
.dqc-resultbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:0 2px 12px;font-size:12.5px;color:var(--muted)}
/* ── Phase 2: two-level decision bar, decide popover, review status, version/history ── */
.dqc-gatebar{display:flex;gap:8px;flex-wrap:wrap;margin:0 0 10px}
.dqc-gseg{all:unset;cursor:pointer;position:relative;min-width:104px;padding:10px 14px 9px;border:1px solid var(--line);
 border-radius:12px;background:var(--card);transition:box-shadow .15s,border-color .15s,transform .08s}
.dqc-gseg:hover{box-shadow:0 3px 10px rgba(16,36,54,.07)}
.dqc-gseg.on{border-color:var(--t);box-shadow:0 0 0 2px color-mix(in srgb,var(--t) 20%,#fff)}
.dqc-gseg .n{display:block;font-family:var(--dqc-mono);font-size:20px;font-weight:600;color:var(--t);line-height:1.15}
.dqc-gseg .l{font-size:11px;color:var(--muted);text-transform:uppercase;letter-spacing:.06em}
.dqc-gseg.rr{margin-left:auto}
.dqc-rgrp{margin:2px 0 6px}
.dqc-rgrp-h{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;font-weight:700;padding:6px 10px 3px;opacity:.85}
.dqc-decide{all:unset;cursor:pointer;display:inline-flex;align-items:center;gap:7px;font-size:12.5px;padding:7px 13px;
 border-radius:9px;background:#f1f4f7;color:#3b4a59;border:1px solid var(--line)}
.dqc-decide:hover{background:#e8edf2}
.dqc-decide[style*="--t"]{background:color-mix(in srgb,var(--t) 10%,#fff);color:var(--t);border-color:color-mix(in srgb,var(--t) 40%,#fff)}
.dqc-decide .dot{width:8px;height:8px;border-radius:50%;background:var(--t)}
.dqc-decide .cv{opacity:.5;font-size:10px}
.dqc-decide.pending{box-shadow:0 0 0 2px color-mix(in srgb,var(--t) 18%,#fff)}
.dqc-bulk{margin-left:auto}
.dqc-pop-scrim{position:fixed;inset:0;z-index:80;background:rgba(14,36,54,.30)}
.dqc-pop{position:fixed;z-index:81;top:50%;left:50%;transform:translate(-50%,-50%);width:min(520px,94vw);
 background:#fff;border-radius:14px;box-shadow:0 24px 64px rgba(0,0,0,.26);overflow:hidden}
.dqc-pop-h{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:13px 16px;
 background:#0e2436;color:#fff;font-size:13.5px;font-weight:600;font-family:var(--dqc-disp)}
.dqc-pop-h .x{all:unset;cursor:pointer;font-size:22px;line-height:1;color:#cdd8ea}.dqc-pop-h .x:hover{color:#fff}
.dqc-pop-gates{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;padding:14px 16px 4px}
.dqc-pop-gates .gbtn{all:unset;cursor:pointer;text-align:center;padding:9px 6px;border-radius:9px;font-size:12.5px;
 font-weight:600;color:#5a6675;background:#f1f4f7;border:1px solid transparent}
.dqc-pop-gates .gbtn:hover{background:#e8edf2}
.dqc-pop-gates .gbtn.on{background:color-mix(in srgb,var(--t) 13%,#fff);color:var(--t);border-color:var(--t)}
.dqc-pop-body{padding:8px 16px 4px}
.dqc-pop-body .lbl{font-size:10px;letter-spacing:.09em;text-transform:uppercase;color:#9aa6b2;font-weight:700;margin:10px 0 6px}
.dqc-pop-body .lbl.req b{color:var(--red)}
.ropt{display:flex;gap:9px;align-items:flex-start;padding:7px 9px;border-radius:8px;cursor:pointer;font-size:13px;color:var(--ink2)}
.ropt:hover{background:#f4f7fa} .ropt.on{background:#eef2f6;color:var(--ink);font-weight:600}
.ropt em{font-style:normal;color:var(--muted);font-weight:400;font-size:11.5px}
.dqc-pop-ta{width:100%;box-sizing:border-box;font:inherit;font-size:13px;padding:9px 11px;border:1px solid var(--line);
 border-radius:9px;resize:vertical}
.dqc-pop-ta:focus{outline:none;border-color:var(--teal);box-shadow:0 0 0 2px #d9ecea}
.dqc-pop-body .warn{font-size:11.5px;color:#a5570f;margin:6px 2px 0}
.dqc-pop-f{display:flex;justify-content:flex-end;gap:9px;padding:12px 16px;border-top:1px solid var(--line);background:#f8fafb}
.dqc-donetag{font-size:10.5px;font-weight:600;color:#fff;border-radius:20px;padding:2px 9px;margin-left:8px}
.dqc-rs{font-size:10.5px;font-weight:600;border-radius:20px;padding:2px 8px;margin-left:6px}
.dqc-rs.rs-re{background:#fbe4d6;color:#a5570f} .dqc-rs.rs-ok{background:#e9f5ee;color:#2f7a4f}
.dqc-ver{display:flex;align-items:center;gap:10px;font-size:12px;color:var(--muted)}
.dqc-ver .v{font-family:var(--dqc-mono);background:#eef1f5;color:#42586e;border-radius:6px;padding:1px 7px;font-weight:600}
.dqc-ver .hlink{all:unset;cursor:pointer;color:#0e7c7b}.dqc-ver .hlink:hover{text-decoration:underline}
.dqc-ver .last{font-size:11.5px}
.dqc-history{border-top:1px solid var(--line);background:#f8fafb;padding:8px 20px 12px;max-height:180px;overflow:auto}
.dqc-history .h-row{display:grid;grid-template-columns:44px 96px 1fr 150px;gap:8px;font-size:11.5px;color:#4a5763;padding:3px 0}
.dqc-history .hv{font-family:var(--dqc-mono);color:#42586e} .dqc-history .hc{color:#0b5f5e;font-weight:600}
.dqc-history .h-row.decision{background:#fbfdfc;border-left:2px solid #cfe0e0;padding-left:6px;border-radius:4px}
.dqc-history .h-row.rule{opacity:.85}
.dqc-history .h-empty{font-size:12px;color:var(--muted);padding:6px 0}
.dqc-history .ha{color:var(--muted);text-align:right}
.dqc-resultbar .cnt b{color:var(--ink);font-family:var(--dqc-mono)}
.dqc-fpill{display:inline-flex;align-items:center;gap:6px;background:#eef2f6;color:var(--ink2);border-radius:20px;padding:3px 10px;font-size:12px}
.dqc-fpill .x{cursor:pointer;color:var(--muted);font-weight:700}
.dqc-fpill .x:hover{color:var(--red)}
.dqc-fchip{display:inline-flex;align-items:baseline;gap:5px;background:#f2f5f8;border-radius:6px;padding:2px 8px;margin:0 6px 6px 0;font-size:12.5px}
.dqc-code.plain{background:#0f3b3a;color:#d6efec}
pre.plainlogic{font-family:var(--dqc-mono);font-size:12px;background:#0f3b3a;color:#d6efec;padding:12px 14px;border-radius:8px;overflow-x:auto;white-space:pre-wrap;margin:0 0 12px}
.dqc-fhead{margin:14px 2px 8px;display:flex;align-items:baseline;gap:10px}
.dqc-fhead .fh-label{font-family:var(--dqc-disp);font-size:14px;font-weight:700;color:var(--ink)}
.dqc-fhead .fh-tech{font-family:var(--dqc-mono);font-size:11px;color:var(--muted)}
.dqc-fhead .fh-n{margin-left:auto;font-size:11px;color:var(--muted);background:#eef2f5;padding:2px 8px;border-radius:20px}
.dqc-card{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:16px 18px;margin-bottom:12px}
.dqc-card:hover{border-color:#cdd6df}
.dqc-card.done-approved{border-left:3px solid #2f7a4f} .dqc-card.done-conditional{border-left:3px solid #b7791f}
.dqc-card.done-pending{border-left:3px solid #43607e} .dqc-card.done-rejected{border-left:3px solid var(--red);opacity:.78}
.dqc-rname{font-family:var(--dqc-disp);font-size:15.5px;font-weight:600;margin-bottom:8px;display:flex;gap:9px;align-items:baseline;flex-wrap:wrap}
.dqc-badges{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:9px}
.dqc-badges .b{font-size:10.5px;font-weight:600;padding:3px 8px;border-radius:20px}
.b.trust.t-confirmed{background:#e6f4ee;color:#0f6b45} .b.trust.t-supported{background:#eaf3fb;color:#1f5fa8}
.b.trust.t-tentative{background:#fdf6e7;color:var(--amber)} .b.trust.t-unverified{background:#f3f0fb;color:#6d4aff}
.b.risk.k-low{background:#eef2f5;color:#42586e} .b.risk.k-moderate{background:#fdf1e3;color:#b7791f} .b.risk.k-high{background:#fdeceb;color:var(--red)}
.b.reason{background:#0e2436;color:#fff} .b.quick{background:#0e7c7b;color:#fff}
.dqc-review{background:#f7f9fb;border:1px solid var(--line);border-radius:10px;padding:12px 14px;margin-bottom:14px}
.dqc-fchip.act{all:unset;cursor:pointer;display:inline-flex;align-items:center;gap:6px;background:#eaf1f6;color:#2c5b7a;
 border:1px solid #cfe0ea;border-radius:20px;padding:3px 11px;font-size:11.5px;margin:0 5px 5px 0}
.dqc-fchip.act:hover{background:#0e7c7b;border-color:#0e7c7b;color:#fff}
.dqc-fchip.act .ic{opacity:.8;font-size:12px}
.dqc-fchip.act .phys{font-family:var(--dqc-mono);opacity:.75}
.dqc-fchip.act:hover .phys{opacity:.9}
.hintk{font-style:normal;font-size:10px;color:#0e7c7b;font-weight:500;letter-spacing:0;text-transform:none}
.dqc-valbtn{all:unset;cursor:pointer;display:inline-flex;align-items:center;gap:6px;background:#0e7c7b;color:#fff;
 border-radius:8px;padding:5px 12px;font-size:12px;font-weight:600;margin:0 6px 6px 0}
.dqc-valbtn:hover{background:#0a6160}
.dqc-mini.vals{border-color:#0e7c7b;color:#0e7c7b;font-weight:600}
.dqc-mini.vals:hover{background:#0e7c7b;color:#fff}
.dqc-smecmt{border-left:3px solid var(--t,#c2ccd6);background:#f8fafb;border-radius:8px;padding:7px 11px;margin:-2px 0 10px}
.dqc-smecmt b{font-size:11.5px;color:var(--t,#42586e)}
.dqc-smecmt .w{font-size:11px;color:var(--muted);margin-left:8px;font-family:var(--dqc-mono)}
.dqc-smecmt .c{font-size:12.5px;color:#33404e;line-height:1.5;font-style:italic;margin-top:3px}
.dqc-smebox{border-left:3px solid var(--t);background:#f8fafb;border-radius:9px;padding:10px 13px;margin-bottom:10px}
.dqc-smebox.none{--t:#c2ccd6}
.dqc-smebox .hd{display:flex;align-items:center;gap:9px;flex-wrap:wrap}
.dqc-smebox .gate{font-size:10px;font-weight:700;letter-spacing:.07em;color:#fff;background:var(--t);border-radius:20px;padding:2px 9px}
.dqc-smebox .cat{font-size:13px;font-weight:600;color:var(--ink)}
.dqc-smebox .who{margin-left:auto;font-size:11.5px;color:var(--muted);font-family:var(--dqc-mono)}
.dqc-smebox .cmt{margin:7px 0 0;font-size:13px;color:#33404e;line-height:1.55;font-style:italic}
.dqc-revised{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;font-weight:600;border-radius:20px;
 padding:2px 9px;margin-left:6px;background:#eef2f8;color:#43607e}
.dqc-dec{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:600;border-radius:20px;
 padding:2px 9px;margin-left:8px;background:color-mix(in srgb,var(--t) 12%,#fff);color:var(--t)}
.dqc-dec .dot{width:7px;height:7px;border-radius:50%;background:var(--t)}
.dqc-triage{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:8px}
.dqc-triage .chip{font-size:11.5px;padding:4px 10px;border-radius:20px;background:#fff;border:1px solid var(--line);color:var(--ink)}
.dqc-triage .chip b{font-weight:700;margin-right:4px;color:var(--muted);text-transform:uppercase;font-size:9.5px;letter-spacing:.05em}
.dqc-triage .chip.trust.t-confirmed{background:#e6f4ee} .dqc-triage .chip.trust.t-tentative{background:#fdf6e7} .dqc-triage .chip.trust.t-unverified{background:#f3f0fb} .dqc-triage .chip.trust.t-supported{background:#eaf3fb}
.dqc-triage .chip.risk.k-high{background:#fdeceb} .dqc-triage .chip.risk.k-moderate{background:#fdf1e3}
.dqc-triage .chip.quick{background:#0e7c7b;color:#fff;border-color:#0e7c7b}
.dqc-todo{background:#fff;border-left:3px solid var(--dqc-brand);padding:8px 12px;border-radius:6px;font-size:13px;margin:6px 0 10px}
.dqc-triexplain{font-size:12px;color:var(--ink2);display:grid;gap:4px;margin-top:6px;opacity:.92}
.dqc-rejreason{font-size:12.5px;color:var(--red);background:#fdeceb;border-radius:6px;padding:6px 10px;margin-right:8px}
.b.fam{background:#eef1f6;color:#42586e} .b.xt{background:#e7f1f1;color:var(--teal-d)} .b.xf{background:#eef2f6;color:#4a6076}
.b.dim{border:1px solid transparent}
.b.dim-completeness{background:#e9f5ee;color:#2f7a4f} .b.dim-validity{background:#eef2f8;color:#43607e}
.b.dim-conformity{background:#eef1f5;color:#586675} .b.dim-consistency{background:#e2f1ef;color:#0b5f5e}
.b.dim-integrity{background:#faedcf;color:#946011} .b.dim-uniqueness{background:#f5f0e4;color:#8a6d1c}
.b.auth-confirmed{background:#e6f4ee;color:#0f6b45} .b.auth-observed{background:#eaf1f8;color:#2c5a8c}
.b.auth-inferred{background:#f4eefb;color:#6a3fb0} .b.auth-none{background:#f2f2f2;color:#777}
.b.conf{font-family:var(--dqc-mono);background:#f5f7f9;color:var(--ink2)}
.b.strength{background:#eef2f6;color:#5a6b7d;text-transform:lowercase}
.b.caveat{background:var(--amber-bg);color:var(--amber)}
/* review-workflow badges — soft-tinted to match the house pill style, no solid fills */
.dqc-badges .b{letter-spacing:.005em;line-height:1.55;display:inline-flex;align-items:center;gap:3px}
.b.rev-obj{background:#fbe9e7;color:#b23a30}
.b.rev-refined{background:#eef2f8;color:#43607e}
.b.rev-ok{background:#e9f5ee;color:#2f7a4f}
.b.suspect{background:#faedcf;color:#946011}
.b.clientok{background:#e6f4ee;color:#0f6b45}
.b.clientval{background:#e2f1ef;color:var(--teal-d)}
.b.clientpend{background:#f5f0e4;color:#8a6d1c}
/* quiet the 'what to do' chip — it was a solid dark block that dominated the card */
.b.reason{background:#eef1f6;color:#37506c;border:1px solid #dde4ec;font-weight:600}
.b.quick{background:#e3f1ef;color:var(--teal-d);border:1px solid #cfe6e2}
/* accurate one-line summary under each rule */
.dqc-note{font-size:12px;color:#586675;margin:-1px 0 11px;padding:7px 11px;background:#f6f8fa;border:1px solid #eef1f5;border-radius:8px;line-height:1.5}
/* card depth + calmer hover */
.dqc-card{box-shadow:0 1px 2px rgba(16,36,54,.035);transition:box-shadow .16s ease,border-color .16s ease}
.dqc-card:hover{border-color:#cfd8e1;box-shadow:0 4px 14px rgba(16,36,54,.07)}
/* collapsible sidebar sections */
.dqc-sec{margin:0}
.dqc-sec + .dqc-sec{border-top:1px solid #eef1f4}
.dqc-sec-h{user-select:none;transition:color .12s,background .12s;cursor:pointer;display:flex;align-items:center;justify-content:space-between;border-radius:7px;padding:9px 8px 8px}
.dqc-sec-h:hover{background:#f4f7fa;color:var(--ink2)}
.dqc-sec-h .chev{opacity:.5;font-size:8px;transition:transform .15s;color:#9aa6b2}
.dqc-clientok{margin-top:10px;padding:9px 12px;border-left:3px solid #1f9d6b;background:#f1faf5;border-radius:8px}
.dqc-clientok .k{margin-bottom:3px;color:#0f6b45;font-weight:600;font-size:12.5px}
.dqc-clientok .dqc-p{margin:2px 0}
.dqc-clientok code{font-family:var(--dqc-mono);font-size:11.5px;background:#e6f4ee;padding:1px 5px;border-radius:4px}
.dqc-clientok.pending{border-left-color:#b7791f;background:#faf6ec}
.dqc-clientok.pending .k{color:#8a6d1c}
.dqc-clientok.pending code{background:#f0e8d5}
/* collapsible sample records in the detail modal */
.dqc-samples{margin-top:12px}
.dqc-sampletoggle{all:unset;cursor:pointer;display:flex;align-items:center;gap:9px;width:100%;box-sizing:border-box;
 padding:9px 12px;border:1px solid var(--line);border-radius:9px;background:var(--paper);font-size:13px;color:var(--ink2)}
.dqc-sampletoggle:hover{background:#eef2f6}
.dqc-sampletoggle .chev{opacity:.5;font-size:10px}
.dqc-sampletoggle .dqc-samplecount{display:inline-flex;gap:6px}
.dqc-sampletoggle em{font-style:normal;font-size:11px;font-weight:600;padding:1px 8px;border-radius:20px}
.dqc-sampletoggle em.f{background:#fbe9e7;color:#b23a30} .dqc-sampletoggle em.p{background:#e9f5ee;color:#2f7a4f}
.dqc-sampletoggle .hint{margin-left:auto;font-size:11.5px;color:var(--muted)}
.dqc-samplebody{border:1px solid var(--line);border-top:none;border-radius:0 0 9px 9px;padding:2px 12px 10px}
.dqc-dispo{margin:10px 0 0;font-size:12.5px;color:var(--amber);background:var(--amber-bg);padding:8px 11px;border-radius:8px}
.dqc-p{font-size:13.5px;color:var(--ink2);margin:0 0 4px}
.dqc-ul{margin:0;padding-left:18px;font-size:13px;color:var(--ink2)} .dqc-ul li{margin:3px 0}
.dqc-caveat{border:1px solid #eccf8f;background:var(--amber-bg);border-radius:10px;padding:12px 14px;margin:6px 0 4px}
.dqc-caveat .ch{font-family:var(--dqc-disp);font-weight:600;font-size:13px;color:#8a5a12;display:flex;gap:8px;align-items:center}
.dqc-caveat .verdict{font-family:var(--dqc-mono);font-size:10.5px;background:#f0dcae;color:#7a4d0d;padding:2px 7px;border-radius:20px;text-transform:uppercase}
.dqc-caveat p{margin:7px 0 0;font-size:13px;color:#6b4e12} .dqc-caveat .rat{color:#7a5a2a;font-size:12.5px}
.dqc-ev{font-size:13px;color:var(--ink2);margin:0 0 5px}
.dqc-dialect{font-family:var(--body);font-size:12px;border:1px solid var(--line);border-radius:6px;padding:3px 8px;color:var(--ink2);background:#fff;text-transform:none;letter-spacing:0}
.dqc-why{color:var(--ink2);font-size:13px;margin-bottom:12px}
.dqc-meta{display:flex;gap:18px;flex-wrap:wrap;font-size:12px;color:var(--muted);margin-bottom:12px}
.dqc-meta b{color:var(--ink2)} .dqc-meta .novel{color:var(--amber);font-weight:600}
.dqc-meta .phys{font-family:var(--dqc-mono);font-size:11px;color:#9aa8b5;margin-left:4px}
.dqc-impact{background:var(--paper);border-radius:10px;padding:11px 13px;margin-bottom:13px}
.dqc-impact .lab{font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted);margin-bottom:7px;display:flex;justify-content:space-between}
.dqc-ibar{height:22px;border-radius:5px;overflow:hidden;display:flex;background:#e9edf1}
.dqc-ibar i{display:block;height:100%} .dqc-ibar .pass{background:var(--green)} .dqc-ibar .flag{background:var(--red)}
.dqc-iline{font-family:var(--dqc-mono);font-size:12px;margin-top:7px;color:var(--ink2)}
.dqc-iline .r{color:var(--red);font-weight:600} .dqc-iline .g{color:var(--green);font-weight:600}
.dqc-impact.un{border:1px dashed var(--line);background:#fafbfc;display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.dqc-impact.un .lab{margin:0;text-transform:none;letter-spacing:0;font-size:12px;color:var(--ink2);font-weight:600}
.dqc-impact.un .untext{font-size:12px;color:var(--muted)}
.dqc-mini{margin-left:auto;font-family:inherit;font-size:12px;font-weight:600;color:var(--teal);background:#fff;border:1px solid #bfe0df;border-radius:7px;padding:6px 12px;cursor:pointer}
.dqc-mini:hover{background:#eff7f6}
.dqc-acts{display:flex;gap:9px;align-items:center}
.dqc-btn.app{background:var(--teal);color:#fff} .dqc-btn.app:hover{background:var(--teal-d)}
.dqc-btn.rej{background:#fff;color:var(--red);border-color:#f0c9c6} .dqc-btn.rej:hover{background:var(--red-bg)}
.dqc-btn.info{background:#fff;color:var(--ink2);border-color:var(--line)} .dqc-btn.info:hover{background:var(--paper)}
.dqc-expand{margin-left:auto;font-size:12px;color:var(--muted);cursor:pointer;font-family:var(--dqc-mono)}
.dqc-detail{margin-top:13px;border-top:1px solid var(--line);padding-top:12px;display:none}
.dqc-detail.open{display:block}
.dqc-detail .k{font-size:10.5px;letter-spacing:.07em;text-transform:uppercase;color:var(--muted);margin-bottom:5px}
.dqc-detail pre,.dqc-code{font-family:var(--dqc-mono);font-size:12px;background:var(--ink);color:#d7e6f2;padding:12px 14px;border-radius:8px;overflow-x:auto;white-space:pre-wrap;margin:0 0 12px}
.dqc-donetag{font-family:var(--dqc-mono);font-size:11px;padding:3px 9px;border-radius:6px}
.dqc-donetag.a{background:#e6f4ee;color:var(--teal-d)} .dqc-donetag.r{background:var(--red-bg);color:var(--red)}
.dqc-tray{position:fixed;left:0;right:0;bottom:0;background:var(--ink);color:#fff;display:flex;align-items:center;gap:14px;padding:12px 24px;box-shadow:0 -4px 24px rgba(14,36,54,.2);z-index:40}
.dqc-tray .t{font-family:var(--dqc-mono);font-size:13px}
.dqc-tray .undo{background:transparent;color:#9db4c9;border:1px solid #33475b;border-radius:8px;padding:9px 16px;cursor:pointer;font-family:inherit}
.dqc-tray .save{background:var(--teal);color:#fff;font-weight:600;border:0;border-radius:8px;padding:9px 20px;cursor:pointer;font-family:inherit}
.dqc-empty{text-align:center;color:var(--muted);padding:50px 20px}
.dqc-flash{position:fixed;bottom:74px;left:50%;transform:translateX(-50%);background:var(--ink);color:#fff;padding:10px 18px;border-radius:10px;font-size:13px;z-index:50}
.dqc-pillrow{display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap}
.dqc-pill{background:#fff;border:1px solid var(--line);border-radius:20px;padding:6px 14px;cursor:pointer;font-size:13px;font-family:inherit}
.dqc-pill.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.dqc-tablewrap{border:1px solid var(--line);border-radius:12px;overflow:hidden;background:#fff}
.dqc-table{width:100%;border-collapse:collapse;font-size:13px}
.dqc-table th{text-align:left;padding:10px 14px;background:var(--paper);color:var(--muted);font-size:11px;text-transform:uppercase;letter-spacing:.06em}
.dqc-table td{padding:10px 14px;border-top:1px solid var(--line)}
.dqc-trow{cursor:pointer} .dqc-trow:hover{background:var(--paper)}
.dqc-panel{border:1px solid var(--line);border-radius:12px;padding:14px;background:#fff}
.dqc-panel h5{margin:12px 0 6px;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:var(--muted)}
.dqc-topval{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px dotted var(--line);font-size:13px}
.dqc-muted{color:var(--muted);font-size:12px}
.dqc-modalbg{position:fixed;inset:0;background:rgba(14,36,54,.5);display:grid;place-items:center;z-index:60;padding:20px}
.dqc-modal{background:#fff;border-radius:14px;width:min(640px,96vw);max-height:88vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.3)}
.dqc-modal.wide{width:min(1440px,97vw)}
.dqc-mgrid{display:grid;grid-template-columns:1fr 1fr;gap:22px;margin-top:12px}
@media (max-width:820px){.dqc-mgrid{grid-template-columns:1fr}}
.dqc-mcol{min-width:0}
.dqc-mcol .k{margin-top:16px}
.dqc-mcol .k:first-child{margin-top:0}
.dqc-plainlogic{background:#f2f7f5;border-left:3px solid var(--dqc-teal,#0e7c7b);border-radius:8px;padding:12px 14px;font-size:14px;line-height:1.5;color:var(--ink)}
.dqc-pseudo{background:#0e2436;color:#d7e5f5;border-radius:8px;padding:12px 14px;font-family:var(--dqc-mono);font-size:12.5px;line-height:1.55;white-space:pre;overflow:auto;margin:0}
.dqc-kv{display:grid;gap:0}
.dqc-kv .kv{display:grid;grid-template-columns:130px 1fr;gap:12px;padding:8px 0;border-bottom:1px solid var(--line)}
.dqc-kv .kv:last-child{border-bottom:none}
.dqc-kv .kk{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:var(--muted);font-weight:600}
.dqc-kv .kvv{font-size:13px;color:var(--ink)}
.dqc-modalh{display:flex;justify-content:space-between;align-items:flex-start;padding:18px 20px;background:var(--ink);color:#fff}
.dqc-x{background:none;border:0;color:#fff;font-size:26px;cursor:pointer;line-height:1}
.dqc-modalbody{padding:18px 20px}
.dqc-modalbody .k{font-size:10.5px;letter-spacing:.07em;text-transform:uppercase;color:var(--muted);margin:16px 0 7px}
.dqc-modalbody .k:first-child{margin-top:0}
.dqc-kpis{display:flex;gap:22px} .dqc-kpis>div{display:flex;flex-direction:column}
.dqc-kpis span{font-family:var(--dqc-mono);font-size:20px;font-weight:600} .dqc-kpis .r{color:var(--red)} .dqc-kpis .g{color:var(--green)}
.dqc-kpis small{color:var(--muted);font-size:11px}
.dqc-row2{display:flex;gap:14px;padding:5px 0;font-size:13px} .dqc-row2 .rk{color:var(--muted);min-width:120px} .dqc-row2 .rv{color:var(--ink2);flex:1}
.dqc-modalf{display:flex;justify-content:flex-end;gap:10px;padding:14px 20px;border-top:1px solid var(--line);position:sticky;bottom:0;background:#fff}
@media(max-width:820px){.dqc-grid{grid-template-columns:1fr}.dqc-rail{position:static}}
`;
